import React, { useEffect, useMemo, useState } from "react";
import usePersistentPageState from "../hooks/usePersistentPageState";
import Layout from "./Layout";
import DateInput from "../components/DateInput";
import { BASE_URL } from "../config";
import { showSuccess, showError } from "../utils/notifications";
import axios from "axios";
import { FaSearch, FaExpandAlt, FaCompressAlt, FaSyncAlt, FaRegCalendarAlt, FaSlidersH, FaCheck, FaChevronDown, FaTimesCircle, FaLocationArrow, FaBan, FaClock, FaFlag, FaCar, FaMale, FaFemale } from "react-icons/fa";
import { MdDoNotDisturbOff } from "react-icons/md";
import { useNavigate, useSearchParams } from "react-router-dom";
import usePageMeta from "../hooks/usePageMeta";
import {
  formatAppDateTime,
  formatAppDistanceToNow,
  formatAppRelativeShortAgo,
  formatAppIntlLabel,
  getAppDisplayTimezone,
  instantFromAppWallParts,
  parseDbDateTime,
  toMysqlUtcWallString,
} from "../utils/appDateTime";
import DateTimePickerPanel from "../components/DateTimePicker.jsx";
import { useAppPermissions } from "../context/PermissionContext";
import {
  AtlasFilterPanel,
  AtlasFilterChipBar,
  useFilterPanel,
  ScheduleFilterFields,
  buildScheduleFilterChips,
} from "../components/filters";
import { AtlasListToolbar } from "../components/list-toolbar";
import { AtlasPageHeader, AtlasPageDivider } from "../components/page-layout";
import {
  ATLAS_BTN_PRIMARY,
  ATLAS_BTN_SECONDARY,
} from "../components/atlasButtonStyles";
import RightPanel from "../components/RightPanel";
import ShiftDetailPanel from "../components/ShiftDetailPanel";
import StaffAvatarCircle from "../components/StaffAvatarCircle";
import PersonnelDetailModal from "../components/PersonnelDetailModal";
import ShiftDetailModal from "../components/ShiftDetailModal";
import CarpoolEditModal from "../components/CarpoolEditModal";
import ScheduleStaffRequestsModal from "../components/ScheduleStaffRequestsModal";
import useStaffingRequestReplicate from "../hooks/useStaffingRequestReplicate";
import {
  getScheduleWeekBounds,
  persistIncludedStaffRequestId,
} from "../utils/scheduleWeekUtils";
import Jobs from "./Jobs";
import {
  shiftRowHasTravel,
  travelPayRateSuffix,
} from "../utils/shiftTravelDisplay";
import {
  formatLegacyScheduleHoursLabel,
  shiftCountsTowardScheduleHours,
  shiftNetWorkHours,
  sumScheduleGroupHours,
} from "../utils/scheduleDurationUtils";

/** Schedule board assignment row avatar (proxy URL via StaffAvatarCircle). */
function renderAvatar(emp) {
  return <StaffAvatarCircle employee={emp} className="h-6 w-6" />;
}

// Full Schedule Page - unified implementation per spec

const formatDate = (d) => {
  const date = new Date(d);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

const scheduleNoteText = (value) =>
  value != null && String(value).trim().length > 0 ? String(value).trim() : "";

/** Shift-level "Note to Temp Staff" (shifts.note / note_to_staff). */
const shiftLevelTempNoteText = (shiftLike) => {
  if (!shiftLike) return "";
  return scheduleNoteText(shiftLike.note_to_staff ?? shiftLike.note ?? "");
};

/** Shift-level admin note (shifts.admin_note). */
const shiftLevelAdminNoteText = (shiftLike) => {
  if (!shiftLike) return "";
  return scheduleNoteText(shiftLike.admin_note ?? "");
};

const entryHasShiftTempNote = (entry) =>
  !!shiftLevelTempNoteText(entry?.rawShift || entry);

const entryHasShiftAdminNote = (entry) =>
  !!shiftLevelAdminNoteText(entry?.rawShift || entry);

const parseLocalDateInput = (value) => {
  if (!value) return null;
  const str = String(value).trim();
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]) - 1;
    const d = Number(m[3]);
    return new Date(y, mo, d);
  }
  const parsed = new Date(str);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

const getDivisionId = (division) =>
  division?.id ?? division?.division_id ?? division?.divisionId ?? division?.value ?? null;

const getDivisionName = (division) => {
  const raw =
    division?.name ??
    division?.division_name ??
    division?.divisionName ??
    division?.label ??
    "";
  return String(raw || "").trim();
};

/** Match filter selection to division row (tolerates trailing spaces in DB or selection). */
const isDivisionSelected = (selectedDivisions, division) => {
  const name = getDivisionName(division);
  if (!name) return false;
  return (selectedDivisions || []).some(
    (s) => String(s ?? "").trim() === name,
  );
};

const parseClientDivisionIds = (raw) => {
  if (Array.isArray(raw))
    return raw.map((x) => Number(x)).filter((n) => Number.isFinite(n));
  if (typeof raw === "string" && raw.trim())
    return raw
      .split(",")
      .map((x) => Number(String(x).trim()))
      .filter((n) => Number.isFinite(n));
  return [];
};

function normalizeScheduleClientRow(c) {
  if (!c || typeof c !== "object") return null;
  const id = c.id ?? c.client_id;
  const name = String(c.name ?? "").trim();
  if (id == null || id === "" || !name) return null;
  return {
    id,
    name,
    divisionIds: parseClientDivisionIds(c.division_ids ?? c.divisionIds),
  };
}

const staffIdKey = (id) =>
  id === null || id === undefined || id === "" ? "" : String(id);

/** DB shift_accounts.state values that should render as confirmed (green) on the board. */
function shiftAccountStateIsScheduleConfirmed(saState) {
  const s = String(saState ?? "").trim().toLowerCase();
  return (
    s === "assigned" ||
    s === "confirmed" ||
    s === "completed" ||
    s === "in_progress" ||
    s === "active" ||
    s === "pending" ||
    s === "filled" ||
    s === "verified" ||
    s === "signed_off"
  );
}

/** Strict confirmed count for Schedule "CONFIRMED" labels (exclude tentative/assigned). */
function assignmentCountsTowardScheduleFilled(status) {
  const s = String(status ?? "").trim().toLowerCase();
  return s === "confirmed" || s === "completed" || shiftAccountStateIsScheduleConfirmed(s);
}

/** Day header "CONFIRMED" should ignore tentative/pending/cancelled shift states. */
function shiftStateCountsTowardConfirmedHeader(shiftStateRaw) {
  const s = String(shiftStateRaw ?? "").trim().toLowerCase();
  if (!s) return true;
  if (s === "tentative" || s === "pending") return false;
  if (s === "cancelled" || s === "declined") return false;
  return true;
}

function applyScheduleEntryHours(entry) {
  const hoursNum = shiftCountsTowardScheduleHours(
    entry.shiftStatus,
    entry.displayStatus,
  )
    ? shiftNetWorkHours(entry.rawShift)
    : 0;
  entry.hoursNum = hoursNum;
  entry.hours = formatLegacyScheduleHoursLabel(hoursNum);
  return entry;
}

// Helper to convert date range label to number of calendar columns (inclusive from start date).
const getDateRangeDaysFromLabel = (label) => {
  switch (label) {
    case "1 Day": return 1;
    case "7 Days": return 7;
    case "14 Days": return 14;
    case "30 Days": return 30;
    default: return 7;
  }
};

function resolveScheduleEmployee(a) {
  return (
    a?.employee ||
    a?.account ||
    a?.assignee ||
    a?.assigned_account ||
    a?.user ||
    a?.person ||
    a?.temp ||
    null
  );
}

/** Raw shift payload for the slot the user opened (parallel merged rows share entry.rawShift from the first shift only). */
function resolveSlotRawShift(entry, assignment) {
  const base = entry?.rawShift || entry?.raw || null;
  const slotId =
    assignment?.sourceShiftId ?? assignment?.raw?.sourceShiftId ?? null;
  if (
    slotId === undefined ||
    slotId === null ||
    slotId === "" ||
    !Array.isArray(entry?.parallelShifts) ||
    entry.parallelShifts.length === 0
  ) {
    return base;
  }
  const found = entry.parallelShifts.find(
    (p) =>
      p &&
      p.id != null &&
      slotId != null &&
      Number(p.id) === Number(slotId),
  );
  return found?.rawShift || base;
}

/** Legacy schedule board sorts rows by parent/system position name, not the client display label. */
function resolveScheduleSlotParentPosition(entry, sourceShiftId) {
  const raw = resolveSlotRawShift(entry, { sourceShiftId });
  const parent =
    raw?.positionParentName ||
    raw?.parent_position_name ||
    raw?.position_parent_name ||
    "";
  if (String(parent).trim()) return String(parent).trim();
  return resolveScheduleSlotPosition(entry, sourceShiftId);
}

function scheduleStaffSortName(emp, assignment) {
  const first = String(emp?.first_name || emp?.firstName || "").trim();
  const last = String(emp?.last_name || emp?.lastName || "").trim();
  if (first || last) return `${first} ${last}`.trim();
  return String(
    emp?.name ||
      emp?.fullName ||
      assignment?.employee?.name ||
      assignment?.name ||
      "",
  ).trim();
}

/** Resolve display position for one parallel slot (merged rows keep entry.position from the first shift only). */
function resolveScheduleSlotPosition(entry, sourceShiftId) {
  const raw = resolveSlotRawShift(entry, { sourceShiftId });
  const fromRaw =
    raw?.positionName ||
    raw?.position_name ||
    raw?.position ||
    "";
  if (String(fromRaw).trim()) return String(fromRaw).trim();
  const sid = sourceShiftId ?? entry?.id ?? entry?.shift_id;
  if (
    sid != null &&
    Array.isArray(entry?.parallelShifts) &&
    entry.parallelShifts.length > 0
  ) {
    const found = entry.parallelShifts.find(
      (p) => p?.id != null && Number(p.id) === Number(sid),
    );
    const par =
      found?.rawShift?.positionName ||
      found?.rawShift?.position_name ||
      found?.rawShift?.position ||
      "";
    if (String(par).trim()) return String(par).trim();
  }
  return String(entry?.position || "").trim();
}

/** Whether a merged schedule slot row includes a given underlying shift id. */
function slotContainsShiftId(slot, shiftIdNum) {
  if (!slot || !Number.isFinite(shiftIdNum)) return false;
  const primary = Number(slot.id ?? slot.shift_id);
  if (Number.isFinite(primary) && primary === shiftIdNum) return true;
  if (Array.isArray(slot.parallelShifts)) {
    if (
      slot.parallelShifts.some(
        (p) => p && p.id != null && Number(p.id) === shiftIdNum,
      )
    ) {
      return true;
    }
  }
  return (slot.assignments || []).some(
    (a) =>
      a &&
      a.sourceShiftId != null &&
      Number(a.sourceShiftId) === shiftIdNum,
  );
}

/** Locate merged time-slot row in the filtered schedule grid by underlying shift id (parallel slots supported). */
function findMergedSlotByShiftId(filteredSchedule, shiftIdNum) {
  const found = findScheduleSlotInGrid(filteredSchedule, shiftIdNum);
  return found?.slot ?? null;
}

/** Like findMergedSlotByShiftId but also returns day/group indices for card expand. */
function findScheduleSlotInGrid(filteredSchedule, shiftIdNum) {
  if (!Array.isArray(filteredSchedule) || !Number.isFinite(shiftIdNum))
    return null;
  for (let dayIdx = 0; dayIdx < filteredSchedule.length; dayIdx++) {
    const dayData = filteredSchedule[dayIdx];
    for (let groupIdx = 0; groupIdx < (dayData.entries || []).length; groupIdx++) {
      const group = dayData.entries[groupIdx];
      for (const slot of group.entries || []) {
        if (slotContainsShiftId(slot, shiftIdNum)) {
          return { slot, dayIdx, groupIdx };
        }
      }
    }
  }
  return null;
}

function buildDeepLinkPanelState(slot, shiftIdNum) {
  const sidNum = Number(shiftIdNum);
  const normalized = buildNormalizedScheduleAssignments(slot);
  const assignment =
    normalized.find((n) => Number(n.sourceShiftId) === sidNum) || null;
  const shiftForPanel =
    resolveSlotRawShift(slot, { sourceShiftId: sidNum }) || slot.rawShift;
  const slotPosition = resolveScheduleSlotPosition(slot, sidNum);
  const entryCopy = {
    ...slot,
    rawShift: shiftForPanel,
    position: slotPosition || slot.position,
    _isUnassignedSlotView: assignment?.status === "unassigned",
  };
  return { entryCopy, assignment, shiftForPanel };
}

/** Collect searchable text for a schedule assignment / account row. */
function scheduleStaffSearchBlob(empOrAcct) {
  if (!empOrAcct) return "";
  const parts = [
    empOrAcct.name,
    empOrAcct.fullName,
    empOrAcct.username,
    empOrAcct.first_name,
    empOrAcct.last_name,
    empOrAcct.firstName,
    empOrAcct.lastName,
    empOrAcct.displayName,
    empOrAcct.staffDisplayName,
    empOrAcct.primary_assignee_name,
    empOrAcct.primaryAssigneeName,
  ];
  const joined = `${empOrAcct.first_name || empOrAcct.firstName || ""} ${empOrAcct.last_name || empOrAcct.lastName || ""}`.trim();
  if (joined) parts.push(joined);
  return parts
    .filter((v) => v != null && String(v).trim() !== "")
    .join(" ")
    .toLowerCase();
}

function scheduleAssignmentMatchesSearch(assignment, q) {
  if (!assignment) return false;
  const emp =
    resolveScheduleEmployee(assignment) ||
    assignment?.employee ||
    assignment?.emp ||
    assignment;
  if (scheduleStaffSearchBlob(emp).includes(q)) return true;
  const empId = String(
    emp?.account_id ??
      emp?.accountId ??
      emp?.id ??
      assignment?.account_id ??
      assignment?.accountId ??
      "",
  );
  return Boolean(empId && empId.includes(q));
}

function scheduleAccountListMatchesSearch(accounts, q) {
  if (!Array.isArray(accounts)) return false;
  for (const acct of accounts) {
    if (scheduleStaffSearchBlob(acct).includes(q)) return true;
    const empId = String(acct?.accountId || acct?.account_id || acct?.id || "");
    if (empId && empId.includes(q)) return true;
  }
  return false;
}

/**
 * Client/location group matches Schedule header search (client, location, staff, or shift id).
 * The whole query is tried first as one phrase (the original behaviour, so "aramark - aviva"
 * still works), then word by word: every word must match somewhere in the group, in any order.
 * Before, "aramark oak" or "aramark aviva oakville" (real name has " - ") hid every group even
 * though /api/schedules already matches per word and returned the right shifts.
 */
function scheduleGroupMatchesSearch(group, searchTrimmed) {
  if (!searchTrimmed) return true;
  const q = searchTrimmed.toLowerCase();
  if (scheduleGroupMatchesSearchTerm(group, q)) return true;
  // Skip punctuation-only words like the "-" in "aramark - aviva".
  const words = q.split(/\s+/).filter((w) => /[\p{L}\p{N}]/u.test(w));
  if (words.length < 2) return false;
  return words.every((w) => scheduleGroupMatchesSearchTerm(group, w));
}

/** One lowercase search term against a group's client, location, position, staff, or shift id. */
function scheduleGroupMatchesSearchTerm(group, q) {
  const loc = String(group?.location ?? "").toLowerCase();
  const sub = String(group?.sublocation ?? "").toLowerCase();
  if (loc.includes(q) || sub.includes(q)) return true;
  for (const slot of group?.entries || []) {
    const idStr = String(slot?.id ?? "").toLowerCase();
    const shiftIdStr = String(slot?.shift_id ?? "").toLowerCase();
    if (idStr.includes(q) || shiftIdStr.includes(q)) return true;

    // Position was never part of this search (client/location/staff/shift-id only), so a
    // position-only query like "server" always returned nothing even though it's visibly on
    // the board -- reuses the same helper the board's own "Client & Position" column uses.
    const slotPosition = resolveScheduleSlotPosition(
      slot,
      slot?.id ?? slot?.shift_id,
    );
    if (slotPosition && slotPosition.toLowerCase().includes(q)) return true;

    const raw = slot?.rawShift || {};
    if (
      scheduleStaffSearchBlob({
        name: raw.primary_assignee_name || raw.primaryAssigneeName,
        first_name: raw.staff_first_name,
        last_name: raw.staff_last_name,
      }).includes(q)
    ) {
      return true;
    }

    const assignedList =
      slot?.assignedAccounts ||
      raw.assignedAccounts ||
      raw.assigned_accounts ||
      [];
    if (scheduleAccountListMatchesSearch(assignedList, q)) return true;

    if (Array.isArray(slot?.assignments)) {
      for (const a of slot.assignments) {
        if (scheduleAssignmentMatchesSearch(a, q)) return true;
      }
    }

    if (Array.isArray(slot?.parallelShifts)) {
      for (const ps of slot.parallelShifts) {
        if (ps && String(ps.id ?? "").toLowerCase().includes(q)) return true;
        const psPosition = resolveScheduleSlotPosition(slot, ps?.id);
        if (psPosition && psPosition.toLowerCase().includes(q)) return true;
        const psRaw = ps?.rawShift || {};
        if (
          scheduleStaffSearchBlob({
            name: psRaw.primary_assignee_name || psRaw.primaryAssigneeName,
          }).includes(q)
        ) {
          return true;
        }
        const psAssigned =
          ps?.assignedAccounts ||
          psRaw.assignedAccounts ||
          psRaw.assigned_accounts ||
          [];
        if (scheduleAccountListMatchesSearch(psAssigned, q)) return true;
      }
    }
  }
  return false;
}

/** Legacy schedule tree sorts client/location groups alphabetically by name. */
function compareScheduleClientGroups(a, b) {
  const locCmp = String(a?.location ?? "").localeCompare(
    String(b?.location ?? ""),
    undefined,
    { sensitivity: "base" },
  );
  if (locCmp !== 0) return locCmp;
  return String(a?.sublocation ?? "").localeCompare(
    String(b?.sublocation ?? ""),
    undefined,
    { sensitivity: "base" },
  );
}

/** Legacy groups shifts by start/end datetime hash — ascending chronological order. */
function scheduleSlotStartMs(slot) {
  if (slot?.slotStartMs != null && Number.isFinite(Number(slot.slotStartMs))) {
    return Number(slot.slotStartMs);
  }
  const raw =
    slot?.rawShift?.start_date ??
    slot?.parallelShifts?.[0]?.rawShift?.start_date ??
    null;
  const t = raw ? new Date(raw).getTime() : NaN;
  return Number.isFinite(t) ? t : 0;
}

function scheduleSlotEndMs(slot) {
  if (slot?.slotEndMs != null && Number.isFinite(Number(slot.slotEndMs))) {
    return Number(slot.slotEndMs);
  }
  const raw =
    slot?.rawShift?.end_date ??
    slot?.parallelShifts?.[0]?.rawShift?.end_date ??
    null;
  const t = raw ? new Date(raw).getTime() : NaN;
  return Number.isFinite(t) ? t : 0;
}

function compareScheduleTimeSlots(a, b) {
  const startCmp = scheduleSlotStartMs(a) - scheduleSlotStartMs(b);
  if (startCmp !== 0) return startCmp;
  return scheduleSlotEndMs(a) - scheduleSlotEndMs(b);
}

function compareScheduleSlotAssignments(entry, a, b) {
  const nameA = scheduleStaffSortName(a?.employee, a);
  const nameB = scheduleStaffSortName(b?.employee, b);
  if (nameA && nameB) {
    const nameCmp = nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
    if (nameCmp !== 0) return nameCmp;
  }
  const posA = resolveScheduleSlotParentPosition(entry, a?.sourceShiftId);
  const posB = resolveScheduleSlotParentPosition(entry, b?.sourceShiftId);
  const posCmp = posA.localeCompare(posB, undefined, { sensitivity: "base" });
  if (posCmp !== 0) return posCmp;
  const sidA = Number(a?.sourceShiftId ?? 0);
  const sidB = Number(b?.sourceShiftId ?? 0);
  return sidA - sidB;
}

function compareScheduleNormalizedRows(entry, a, b) {
  const nameA = scheduleStaffSortName(a?.emp, a?.raw);
  const nameB = scheduleStaffSortName(b?.emp, b?.raw);
  if (nameA && nameB) {
    const nameCmp = nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
    if (nameCmp !== 0) return nameCmp;
  }
  const posA = resolveScheduleSlotParentPosition(entry, a?.sourceShiftId);
  const posB = resolveScheduleSlotParentPosition(entry, b?.sourceShiftId);
  const posCmp = posA.localeCompare(posB, undefined, { sensitivity: "base" });
  if (posCmp !== 0) return posCmp;
  if (a?.status === "unassigned" && b?.status !== "unassigned") return 1;
  if (b?.status === "unassigned" && a?.status !== "unassigned") return -1;
  return Number(a?.sourceShiftId ?? 0) - Number(b?.sourceShiftId ?? 0);
}

function scheduleLegacyGenderBucket(row) {
  const rawGender =
    row?.raw?.gender ??
    row?.raw?.requiredGender ??
    row?.raw?.genderRequirement ??
    row?.raw?.sex ??
    row?.emp?.gender ??
    "";
  const normalized = String(rawGender).trim().toLowerCase();
  if (normalized === "female") return "female";
  if (normalized === "male") return "male";
  return "any";
}

function scheduleLegacyBucket(row) {
  const status = String(row?.status || "").toLowerCase();
  const emp = row?.emp || null;
  if (status === "cancelled" || status === "declined") return "cancelled";
  if (!emp || status === "unassigned") return "unassigned";
  return "assigned";
}

function buildLegacyScheduleDisplayRows(entry, normalizedRows) {
  const rows = Array.isArray(normalizedRows) ? normalizedRows : [];
  const positionOrder = [];
  const grouped = new Map();

  rows.forEach((row) => {
    const slotId = row?.sourceShiftId ?? row?.raw?.sourceShiftId ?? null;
    const positionLabel = String(
      resolveScheduleSlotPosition(entry, slotId) ||
        row?.raw?.role ||
        row?.raw?.position ||
        entry?.position ||
        "",
    ).trim();
    const parentLabel = String(
      resolveScheduleSlotParentPosition(entry, slotId) || positionLabel,
    ).trim();
    const positionKey =
      String(row?.raw?.positionId ?? row?.raw?.position_id ?? "").trim() ||
      positionLabel ||
      `slot-${slotId ?? "unknown"}`;

    if (!grouped.has(positionKey)) {
      grouped.set(positionKey, {
        key: positionKey,
        positionLabel,
        parentLabel,
        rows: [],
      });
      positionOrder.push(positionKey);
    }

    grouped.get(positionKey).rows.push({
      ...row,
      _schedulePositionLabel: positionLabel,
      _scheduleParentLabel: parentLabel,
      _schedulePositionKey: positionKey,
    });
  });

  const orderedGroups = positionOrder
    .map((key) => grouped.get(key))
    .sort((a, b) =>
      a.parentLabel.localeCompare(b.parentLabel, undefined, {
        sensitivity: "base",
      }),
    );

  const bucketPriority = { assigned: 0, unassigned: 1, cancelled: 2 };
  const genderPriority = { any: 0, female: 1, male: 2 };
  const positionCounts = {};
  const output = [];

  orderedGroups.forEach((group) => {
    const sortedRows = [...group.rows].sort((a, b) => {
      const bucketCmp =
        bucketPriority[scheduleLegacyBucket(a)] -
        bucketPriority[scheduleLegacyBucket(b)];
      if (bucketCmp !== 0) return bucketCmp;

      const genderCmp =
        genderPriority[scheduleLegacyGenderBucket(a)] -
        genderPriority[scheduleLegacyGenderBucket(b)];
      if (genderCmp !== 0) return genderCmp;

      const nameCmp = scheduleStaffSortName(a?.emp, a?.raw).localeCompare(
        scheduleStaffSortName(b?.emp, b?.raw),
        undefined,
        { sensitivity: "base" },
      );
      if (nameCmp !== 0) return nameCmp;

      return Number(a?.sourceShiftId ?? 0) - Number(b?.sourceShiftId ?? 0);
    });

    sortedRows.forEach((row) => {
      const genderKey = scheduleLegacyGenderBucket(row);
      if (!positionCounts[group.key]) {
        positionCounts[group.key] = { any: 0, female: 0, male: 0 };
      }
      positionCounts[group.key][genderKey] += 1;
      const slotNumber = positionCounts[group.key][genderKey];
      const displaySlotLabel = group.positionLabel
        ? `${group.positionLabel} ${slotNumber}`
        : `Open slot ${slotNumber}`;

      output.push({
        ...row,
        _schedulePositionLabel: group.positionLabel,
        _scheduleParentLabel: group.parentLabel,
        _schedulePositionKey: group.key,
        _scheduleSlotNumber: slotNumber,
        _scheduleDisplaySlotLabel: displaySlotLabel,
      });
    });
  });

  return output;
}

function pickAssignmentFromSlot(slot, prevAssignment, fallbackShiftId) {
  const list = slot.assignments || [];
  if (!prevAssignment || !list.length) return null;
  const wantSid = Number(
    prevAssignment.sourceShiftId ??
      prevAssignment.raw?.sourceShiftId ??
      fallbackShiftId,
  );
  const wantAcc = Number(
    prevAssignment.emp?.account_id ||
      prevAssignment.employee?.account_id ||
      prevAssignment.raw?.employee?.account_id ||
      prevAssignment.account_id,
  );
  if (Number.isFinite(wantAcc) && wantAcc > 0) {
    const bySidAndAcc = list.find((a) => {
      const asid = Number(a.sourceShiftId ?? a.raw?.sourceShiftId);
      if (!Number.isFinite(wantSid) || asid !== wantSid) return false;
      const aid = Number(
        a.emp?.account_id ||
          a.employee?.account_id ||
          a.raw?.employee?.account_id,
      );
      return aid === wantAcc;
    });
    if (bySidAndAcc) return bySidAndAcc;
  } else {
    const filledForSid = list.find((a) => {
      const asid = Number(a.sourceShiftId ?? a.raw?.sourceShiftId);
      if (!Number.isFinite(wantSid) || asid !== wantSid) return false;
      const emp = a.employee || a.emp;
      return !!(emp && (emp.account_id || emp.id));
    });
    if (filledForSid) return filledForSid;
  }
  return (
    list.find(
      (a) =>
        Number(a.sourceShiftId ?? a.raw?.sourceShiftId) === wantSid,
    ) || null
  );
}

/** Real shifts.state for one parallel slot (merged card keeps first shift on entry until applyMergedParallelShiftState). */
function getSlotShiftStateRaw(entry, sourceShiftId) {
  if (
    sourceShiftId != null &&
    sourceShiftId !== "" &&
    Array.isArray(entry?.parallelShifts) &&
    entry.parallelShifts.length > 0
  ) {
    const p = entry.parallelShifts.find(
      (x) =>
        x &&
        x.id != null &&
        Number(x.id) === Number(sourceShiftId),
    );
    if (p && p.shiftStatus != null && String(p.shiftStatus).trim() !== "") {
      return String(p.shiftStatus).toLowerCase().trim();
    }
  }
  return String(entry?.shiftStatus || "").toLowerCase().trim();
}

/**
 * When several shifts share the same time, entry.shiftStatus/displayStatus came from the first only.
 * Promote cancelled/declined if any parallel shift is in that state so the card header matches Jobs.
 */
function applyMergedParallelShiftState(slot) {
  const par = slot.parallelShifts || [];
  if (!Array.isArray(par) || par.length <= 1) return slot;
  const states = par.map((p) =>
    String(p.shiftStatus || "")
      .toLowerCase()
      .trim(),
  );
  let mergedSt = String(slot.shiftStatus || "").toLowerCase().trim();
  if (states.some((s) => s === "cancelled")) mergedSt = "cancelled";
  else if (states.some((s) => s === "declined")) mergedSt = "declined";
  else if (
    states.length > 0 &&
    states.every((s) => s === "confirmed" || s === "approved")
  ) {
    mergedSt = "confirmed";
  }
  slot.shiftStatus = mergedSt;
  const ac = slot.assignedCount || 0;
  let displayStatus;
  if (mergedSt === "declined") displayStatus = "Unfilled";
  else if (mergedSt === "cancelled") displayStatus = "Cancelled";
  else if (mergedSt === "approved" || mergedSt === "confirmed")
    displayStatus = "Confirmed";
  else if (mergedSt === "tentative")
    displayStatus = ac === 0 ? "Unassigned" : "Tentative";
  else displayStatus = ac === 0 ? "Unassigned" : "Confirmed";
  slot.displayStatus = displayStatus;
  return slot;
}

/** Parallel shifts merged into one Schedule row share one entry.id; each slot needs its real shift id for assign API. */
function pickShiftIdForPadding(entry, normalizedSoFar) {
  const list =
    Array.isArray(entry.parallelShifts) && entry.parallelShifts.length > 0
      ? entry.parallelShifts
      : [
          {
            id: entry.id || entry.shift_id,
            requiredStaff: Math.max(1, Number(entry.requiredStaff) || 1),
          },
        ];
  const countFor = (id) =>
    normalizedSoFar.filter((row) => {
      const sid = row.sourceShiftId ?? row.raw?.sourceShiftId;
      return sid != null && sid !== "" && Number(sid) === Number(id);
    }).length;
  for (const m of list) {
    const id = m.id;
    const cap = Math.max(0, Number(m.requiredStaff) || 0);
    if (cap <= 0) continue;
    if (countFor(id) < cap) return id;
  }
  return list[0]?.id ?? entry.id ?? entry.shift_id;
}

const SCHEDULE_PLACEHOLDER_EMAIL = "unassigned@local.internal";

/** Active assignee on the schedule board (excludes placeholder slots and history-only rows). */
function isRealScheduleAssignment(acct) {
  if (!acct) return false;
  const accountId = Number(acct.accountId || acct.account_id || acct.id);
  if (!Number.isFinite(accountId) || accountId <= 0) return false;
  const state = String(acct.state || acct.status || "").toLowerCase();
  if (state === "unassigned" || state === "unassigned_history") return false;
  const email = String(acct.email || "").trim().toLowerCase();
  if (email === SCHEDULE_PLACEHOLDER_EMAIL) return false;
  const name = String(acct.name || "").trim();
  if (name === "Unassigned Account") return false;
  return true;
}

/** Drop history rows for staff who are actively assigned again on the same shift. */
function filterUnassignedHistoryExcludingActiveAssignees(history, assignedList) {
  const activeIds = new Set();
  for (const acct of assignedList || []) {
    if (!isRealScheduleAssignment(acct)) continue;
    const aid = Number(acct.accountId || acct.account_id || acct.id);
    if (Number.isFinite(aid) && aid > 0) activeIds.add(aid);
  }
  if (!Array.isArray(history) || history.length === 0) return [];
  if (activeIds.size === 0) return history;
  return history.filter((h) => {
    const aid = Number(h?.accountId ?? h?.account_id);
    return !(Number.isFinite(aid) && aid > 0 && activeIds.has(aid));
  });
}

function unassignReasonLabel(code) {
  const c = String(code || "").toLowerCase();
  if (c === "sick") return "Sick";
  if (c === "mia") return "MIA";
  return "Other";
}

function formatUnassignAgo(modifyDate) {
  const result = formatAppRelativeShortAgo(modifyDate, {
    emptyValue: formatAppDistanceToNow(modifyDate),
  });
  return result;
}

function scheduleHistoryForShift(entry, shiftId) {
  const history =
    entry?.unassignedHistory || entry?.rawShift?.unassignedHistory || [];
  if (!Array.isArray(history) || history.length === 0) return [];
  const sid = shiftId != null ? String(shiftId) : "";
  let filtered = !sid
    ? history
    : history.filter(
        (h) =>
          String(h.sourceShiftId ?? entry.id ?? entry.shift_id ?? "") === sid,
      );
  const activeOnShift = new Set();
  for (const a of entry?.assignments || []) {
    const emp = a?.employee;
    if (!emp) continue;
    const aid = Number(emp.account_id || emp.id);
    const aSid = String(a?.sourceShiftId ?? entry?.id ?? entry?.shift_id ?? "");
    if (sid && aSid && aSid !== sid) continue;
    const st = String(a?.status || a?.state || "").toLowerCase();
    if (st === "unassigned" || st === "unassigned_history") continue;
    if (Number.isFinite(aid) && aid > 0) activeOnShift.add(aid);
  }
  if (activeOnShift.size === 0) return filtered;
  return filtered.filter((h) => {
    const aid = Number(h?.accountId ?? h?.account_id);
    return !(Number.isFinite(aid) && aid > 0 && activeOnShift.has(aid));
  });
}

function buildNormalizedScheduleAssignments(entry, resolveEmployee = resolveScheduleEmployee) {
  const rawAssignments = entry.assignments || [];
  const required = entry.requiredStaff || 1;
  const normalized = (Array.isArray(rawAssignments) ? rawAssignments : []).map((a) => {
    const preStatus = (a?.status || "").toString().toLowerCase();
    const saState = (a?.raw?.state || a?.state || "").toString().toLowerCase();
    const legacyConfirmed =
      a?.confirmed === true ||
      a?.confirmed === 1 ||
      a?.confirmed === "1" ||
      a?.raw?.confirmed === true ||
      a?.raw?.confirmed === 1;
    let normalizedStatus;
    if (
      preStatus === "confirmed" ||
      shiftAccountStateIsScheduleConfirmed(saState) ||
      legacyConfirmed
    ) {
      normalizedStatus = "confirmed";
    } else if (
      preStatus === "assigned" ||
      saState === "assigned" ||
      saState === "accepted" ||
      preStatus === "accepted"
    ) {
      normalizedStatus = "assigned";
    } else if (preStatus === "unassigned" || saState === "unassigned" || !resolveEmployee(a)) {
      normalizedStatus = "unassigned";
    } else {
      normalizedStatus = "pending";
    }
    return {
      raw: a,
      emp: resolveEmployee(a),
      status: normalizedStatus,
      sourceShiftId: a?.sourceShiftId,
      hasTempNote: !!a?.hasTempNote,
      hasAdminNote: !!a?.hasAdminNote,
    };
  });
  while (normalized.length < required) {
    const sid = pickShiftIdForPadding(entry, normalized);
    const slotRole = resolveScheduleSlotPosition(entry, sid);
    normalized.push({
      raw: { sourceShiftId: sid, status: "unassigned", role: slotRole },
      emp: null,
      status: "unassigned",
      sourceShiftId: sid,
    });
  }
  normalized.sort((a, b) => compareScheduleNormalizedRows(entry, a, b));
  return normalized;
}

function getScheduleEntryDisplayRows(
  entry,
  resolveEmployee = resolveScheduleEmployee,
) {
  if (!entry) return [];
  const normalized = buildNormalizedScheduleAssignments(entry, resolveEmployee);
  return buildLegacyScheduleDisplayRows(entry, normalized);
}

export default function Schedule() {
  usePageMeta("Schedule");
  const { canWriteShifts } = useAppPermissions();
  const shiftsReadOnly = !canWriteShifts;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = usePersistentPageState("page_search_schedule", "");
  const [loading, setLoading] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});
  const [showShiftPanel, setShowShiftPanel] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedAssignment, setSelectedAssignment] = useState(null); // NEW: for subcard
  const [showPersonnelModal, setShowPersonnelModal] = useState(false);
  const [selectedPersonnel, setSelectedPersonnel] = useState(null);
  const [showShiftDetailModal, setShowShiftDetailModal] = useState(false);
  const [selectedShiftForModal, setSelectedShiftForModal] = useState(null);

  // Simple note viewer modal for schedule view
  const [noteModal, setNoteModal] = useState(null); // { type: 'temp' | 'admin', tempText, adminText }

  const [carpoolViewer, setCarpoolViewer] = useState({
    open: false,
    name: "",
    note: "",
  });

  // Bulk selection state - track individual assignments, not shifts
  const [selectedAssignmentIds, setSelectedAssignmentIds] = useState(new Set()); // Set of "shiftId_accountId" or "shiftId_unassigned_index"
  const [daySelectAll, setDaySelectAll] = useState({}); // { dayIdx: boolean }
  const [bulkModeActive, setBulkModeActive] = useState(false);
  const [staffRequestsModal, setStaffRequestsModal] = useState(null);
  const [staffRequestsRefreshKey, setStaffRequestsRefreshKey] = useState(0);
  const staffRequestsReplicateContextRef = React.useRef(null);
  const [embeddedStaffRequest, setEmbeddedStaffRequest] = useState(null);

  // Refs for synchronized scrolling
  const scrollbarRef = React.useRef(null);
  const contentRef = React.useRef(null);

  // Synchronized scrolling handlers
  const handleScrollbarScroll = () => {
    if (scrollbarRef.current && contentRef.current) {
      contentRef.current.scrollLeft = scrollbarRef.current.scrollLeft;
    }
  };

  const handleContentScroll = () => {
    if (scrollbarRef.current && contentRef.current) {
      scrollbarRef.current.scrollLeft = contentRef.current.scrollLeft;
    }
  };

  const handleOpenPersonnelModal = (staffMember) => {
    // Prefetch full account information where possible so the modal is populated
    (async () => {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const acctId = staffMember?.account_id || staffMember?.id || staffMember?.accountId || null;
      // Only attempt prefetch when we have a plausible id
      if (!acctId) {
        setSelectedPersonnel(staffMember);
        setShowPersonnelModal(true);
        return;
      }

      try {
        const res = await fetch(`${BASE_URL}/api/personnel/accounts/${acctId}`, { headers });
        if (res.ok) {
          const json = await res.json().catch(() => null);
          const data = json && json.data ? json.data : null;
          if (data) {
            const full = { id: data.account_id || data.accountId || acctId, ...data };
            // Ensure `name` exists for the modal header
            if (!full.name) full.name = [full.first_name, full.last_name].filter(Boolean).join(' ').trim();
            setSelectedPersonnel(full);
            setShowPersonnelModal(true);
            return;
          }
        }
        // If we get here, fallback to lightweight object
        console.warn('Prefetch account failed, falling back to lightweight staff object', { status: res && res.status });
      } catch (e) {
        console.warn('Error prefetching personnel account, falling back', e);
      }
      setSelectedPersonnel(staffMember);
      setShowPersonnelModal(true);
    })();
  };

  const handleOpenShiftModal = (shift, entry) => {
    setSelectedShiftForModal(shift || entry);
    setShowShiftDetailModal(true);
  };

  const getAuthToken = () => localStorage.getItem("authToken") || localStorage.getItem("token");
  const getAuthHeaders = () => {
    const token = getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleShowShiftNote = async (shiftId, _accountId, type) => {
    if (!shiftId) return;
    try {
      const resp = await fetch(`${BASE_URL}/api/jobs/shifts/${shiftId}`, {
        credentials: "include",
        headers: { ...getAuthHeaders() },
      });
      const json = await resp.json().catch(() => null);
      const data = (json && (json.data || json)) || {};
      const tempText = shiftLevelTempNoteText(data);
      const adminText = shiftLevelAdminNoteText(data);

      setNoteModal({
        type,
        tempText,
        adminText,
      });
    } catch (e) {
      console.error("Failed to load note:", e);
      setNoteModal({
        type,
        tempText: "",
        adminText: "",
      });
    }
  };

  // --- Persisted filter state (survives page navigation) ---
  const defaultScheduleFilters = {
    selectedDivisions: [],
    selectedAssignmentType: "",
    selectedAgencies: [],
    selectedEmploymentOffices: [],
    selectedClients: [],
    selectedStaffIds: [],
    selectedPositions: [],
    selectedDate: "",
    selectedDateRange: "7 Days",
    selectedShiftStatuses: [],
  };

  const [scheduleFilters, setScheduleFilters] = usePersistentPageState(
    "page_filters_schedule",
    defaultScheduleFilters,
  );

  // Convenience getters — read from the persisted bundle
  const selectedDivisions = scheduleFilters.selectedDivisions ?? [];
  const selectedAssignmentType = scheduleFilters.selectedAssignmentType ?? "";
  const selectedAgencies = scheduleFilters.selectedAgencies ?? [];
  const selectedEmploymentOffices = scheduleFilters.selectedEmploymentOffices ?? [];
  const selectedClients = scheduleFilters.selectedClients ?? [];
  const selectedStaffIds = scheduleFilters.selectedStaffIds ?? [];
  const selectedPositions = scheduleFilters.selectedPositions ?? [];
  const selectedDate = scheduleFilters.selectedDate ?? "";
  const selectedDateRange = scheduleFilters.selectedDateRange ?? "7 Days";
  const selectedShiftStatuses = scheduleFilters.selectedShiftStatuses ?? [];

  // Convenience setters — write into the persisted bundle
  const setSelectedDivisions = (val) => setScheduleFilters((prev) => ({ ...prev, selectedDivisions: typeof val === "function" ? val(prev.selectedDivisions ?? []) : val }));
  const setSelectedAssignmentType = (val) => setScheduleFilters((prev) => ({ ...prev, selectedAssignmentType: typeof val === "function" ? val(prev.selectedAssignmentType ?? "") : val }));
  const setSelectedAgencies = (val) => setScheduleFilters((prev) => ({ ...prev, selectedAgencies: typeof val === "function" ? val(prev.selectedAgencies ?? []) : val }));
  const setSelectedEmploymentOffices = (val) => setScheduleFilters((prev) => ({ ...prev, selectedEmploymentOffices: typeof val === "function" ? val(prev.selectedEmploymentOffices ?? []) : val }));
  const setSelectedClients = (val) => setScheduleFilters((prev) => ({ ...prev, selectedClients: typeof val === "function" ? val(prev.selectedClients ?? []) : val }));
  const setSelectedStaffIds = (val) => setScheduleFilters((prev) => ({ ...prev, selectedStaffIds: typeof val === "function" ? val(prev.selectedStaffIds ?? []) : val }));
  const setSelectedPositions = (val) => setScheduleFilters((prev) => ({ ...prev, selectedPositions: typeof val === "function" ? val(prev.selectedPositions ?? []) : val }));
  const setSelectedDate = (val) => setScheduleFilters((prev) => ({ ...prev, selectedDate: typeof val === "function" ? val(prev.selectedDate ?? "") : val }));
  const setSelectedDateRange = (val) => setScheduleFilters((prev) => ({ ...prev, selectedDateRange: typeof val === "function" ? val(prev.selectedDateRange ?? "7 Days") : val }));
  const setSelectedShiftStatuses = (val) => setScheduleFilters((prev) => ({ ...prev, selectedShiftStatuses: typeof val === "function" ? val(prev.selectedShiftStatuses ?? []) : val }));

  // Filter dropdown data from database (stored as objects with id/name)
  const dateRanges = ["1 Day", "7 Days", "14 Days", "30 Days"];
  const [divisions, setDivisions] = useState([]);
  const [assignmentTypes, setAssignmentTypes] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [employmentOffices, setEmploymentOffices] = useState([]);
  const [clients, setClients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [positions, setPositions] = useState([]);
  const [shiftStatuses, setShiftStatuses] = useState([]);


  // Schedule data state
  const [scheduleData, setScheduleData] = useState([]);
  const [refreshTick, setRefreshTick] = useState(0);

  const { openReplicateRequestModal, ReplicateModal } =
    useStaffingRequestReplicate({
      onSuccess: (result) => {
        const newId = Number(result?.newStaffRequestId);
        const ctx = staffRequestsReplicateContextRef.current;
        if (Number.isFinite(newId) && newId > 0 && ctx?.clientId) {
          const bounds = getScheduleWeekBounds(ctx.weekAnchorDate) || {};
          persistIncludedStaffRequestId(
            ctx.clientId,
            bounds.shiftDateFrom,
            bounds.shiftDateTo,
            newId,
          );
        }
        setRefreshTick((t) => t + 1);
        setStaffRequestsRefreshKey((k) => k + 1);
      },
      onOpenNewRequest: (detailRequest) => {
        const id = detailRequest?.id || detailRequest?.staff_request_id;
        if (!id) return;
        // Keep staff-requests list open underneath; user returns here after closing embed.
        setEmbeddedStaffRequest({ id, row: detailRequest });
      },
    });

  const filterPanel = useFilterPanel();
  const [filterOptionsLoaded, setFilterOptionsLoaded] = useState(false);
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false);
  const [staffFilterLoading, setStaffFilterLoading] = useState(false);
  const [staffSearchQuery, setStaffSearchQuery] = useState("");

  // Load filter option lists on mount
  useEffect(() => {
    if (filterOptionsLoaded || filterOptionsLoading) return;

    const fetchLazyFilterData = async () => {
      setFilterOptionsLoading(true);
      console.log("🔍 Schedule: Fetching filter data...");
      try {
        const fetchJSON = async (endpoint) => {
          const res = await fetch(`${BASE_URL}${endpoint}`);
          if (!res.ok) throw new Error(`HTTP ${res.status} for ${endpoint}`);
          return res.json();
        };

        const [
          divisionsRes,
          agenciesRes,
          officesRes,
          clientsRes,
          positionsRes,
          statusesRes,
          assignmentTypesRes,
        ] = await Promise.allSettled([
          fetchJSON("/api/schedules/divisions"),
          fetchJSON("/api/schedules/agencies"),
          fetchJSON("/api/schedules/employment-offices"),
          fetchJSON("/api/schedules/clients"),
          fetchJSON("/api/schedules/positions"),
          fetchJSON("/api/schedules/shift-statuses"),
          fetchJSON("/api/schedules/assignment-types"),
        ]);

        if (divisionsRes.status === "fulfilled" && divisionsRes.value?.data) {
          const loaded = (divisionsRes.value.data || []).map((d) => ({
            ...d,
            name: String(d?.name ?? "").trim(),
          }));
          setDivisions(loaded);
        }
        if (agenciesRes.status === "fulfilled" && agenciesRes.value?.data) {
          setAgencies(agenciesRes.value.data);
        }
        if (officesRes.status === "fulfilled") {
          const val = officesRes.value;
          const list = Array.isArray(val) ? val : val?.data;
          if (Array.isArray(list)) setEmploymentOffices(list);
        }
        if (clientsRes.status === "fulfilled") {
          const val = clientsRes.value;
          const list = Array.isArray(val) ? val : val?.data;
          if (Array.isArray(list)) {
            setClients(list.map(normalizeScheduleClientRow).filter(Boolean));
          }
        }
        if (positionsRes.status === "fulfilled" && positionsRes.value?.data) {
          setPositions(positionsRes.value.data);
        }
        if (statusesRes.status === "fulfilled" && statusesRes.value?.data) {
          setShiftStatuses(statusesRes.value.data);
        }
        if (assignmentTypesRes.status === "fulfilled" && assignmentTypesRes.value?.data) {
          setAssignmentTypes(assignmentTypesRes.value.data);
        }

        setFilterOptionsLoaded(true);
        console.log("✅ Schedule: Filter data fetch completed successfully");
      } catch (error) {
        console.error("❌ Schedule: Error fetching filter data:", error);
      } finally {
        setFilterOptionsLoading(false);
      }
    };

    fetchLazyFilterData();
  }, [filterOptionsLoaded, filterOptionsLoading]);

  // Load and query staff options when search changes
  useEffect(() => {
    const fetchStaffList = async () => {
      setStaffFilterLoading(true);
      try {
        const url = `${BASE_URL}/api/schedules/staff${
          staffSearchQuery ? `?search=${encodeURIComponent(staffSearchQuery)}` : ""
        }`;
        const res = await fetch(url);
        if (res.ok) {
          const json = await res.json();
          if (json?.data && Array.isArray(json.data)) {
            setStaff(json.data);
          }
        }
      } catch (err) {
        console.error("Failed to fetch staff list:", err);
      } finally {
        setStaffFilterLoading(false);
      }
    };

    fetchStaffList();
  }, [staffSearchQuery]);

  // Helper to map account.type or selectedPersonnel.staffType to modal type
  const mapAccountToModalType = (person) => {
    if (!person) return 'office';
    const raw = (person.staffType || person.type || '').toString().toLowerCase();
    if (!raw) return 'office';
    if (raw.includes('field') || raw.includes('temp') || raw.includes('outsourced') || raw.includes('candidate')) return 'temp';
    return 'office';
  };

  // Compute date range based on selected date and date range
  const computeDateRange = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let actualStart;

    // If user selected a specific date, use that (can be past or future)
    if (selectedDate) {
      actualStart = parseLocalDateInput(selectedDate);
      if (!actualStart) {
        actualStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      }
    } else {
      // Default to today if no date selected
      actualStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    }

    const days = selectedDateRange ? getDateRangeDaysFromLabel(selectedDateRange) : 7;
    const end = new Date(actualStart);
    end.setDate(actualStart.getDate() + (days - 1));

    const result = {
      start: formatDate(actualStart),
      end: formatDate(end),
      days,
    };

    console.log('🗓️ Date Range:', {
      today: formatDate(today),
      actualStart: result.start,
      end: result.end,
      daysToShow: days
    });

    return result;
  }, [selectedDate, selectedDateRange]);

  // Client dropdown: all clients when no division selected; else only clients mapped to selected division(s)
  const clientsForFilterModal = useMemo(() => {
    if (!selectedDivisions.length) return clients;
    const divisionIds = divisions
      .filter((d) => isDivisionSelected(selectedDivisions, d))
      .map((d) => Number(getDivisionId(d)))
      .filter((n) => Number.isFinite(n));
    if (!divisionIds.length) return clients;
    return clients.filter((c) => {
      const cds = c.divisionIds;
      return (
        Array.isArray(cds) &&
        cds.length > 0 &&
        cds.some((id) => divisionIds.includes(Number(id)))
      );
    });
  }, [clients, divisions, selectedDivisions]);

  useEffect(() => {
    if (!selectedDivisions.length || !clients.length || !divisions.length) return;
    const divisionIds = divisions
      .filter((d) => isDivisionSelected(selectedDivisions, d))
      .map((d) => Number(getDivisionId(d)))
      .filter((n) => Number.isFinite(n));
    if (!divisionIds.length) return;
    setSelectedClients((prev) =>
      prev.filter((name) => {
        const c = clients.find((x) => x.name === name);
        if (!c || !Array.isArray(c.divisionIds) || !c.divisionIds.length)
          return false;
        return c.divisionIds.some((id) => divisionIds.includes(Number(id)));
      }),
    );
  }, [selectedDivisions, divisions, clients]);

  /** Staff dropdown: when Agency is selected, only staff whose Payroll Payer (accounts.payer_id) matches. */
  const staffForFilterModal = useMemo(() => {
    if (!selectedAgencies.length) return staff;
    const payerIdSet = new Set(
      agencies
        .filter((a) => selectedAgencies.includes(a.name))
        .map((a) => a.id)
        .filter((id) => id != null && `${id}`.trim() !== "")
        .map((id) => String(id)),
    );
    if (!payerIdSet.size) return staff;
    return staff.filter((s) => {
      const raw = s.payerId ?? s.payer_id;
      if (raw == null || raw === "") return false;
      return payerIdSet.has(String(raw));
    });
  }, [staff, agencies, selectedAgencies]);

  useEffect(() => {
    if (!selectedAgencies.length || !staff.length || !agencies.length) return;
    const payerIdSet = new Set(
      agencies
        .filter((a) => selectedAgencies.includes(a.name))
        .map((a) => a.id)
        .filter((id) => id != null && `${id}`.trim() !== "")
        .map((id) => String(id)),
    );
    if (!payerIdSet.size) return;
    setSelectedStaffIds((prev) =>
      prev.filter((id) => {
        const m = staff.find((s) => staffIdKey(s.id) === staffIdKey(id));
        if (!m) return false;
        const raw = m.payerId ?? m.payer_id;
        if (raw == null || raw === "") return false;
        return payerIdSet.has(String(raw));
      }),
    );
  }, [selectedAgencies, agencies, staff]);

  // Fetch schedule data from backend
  const fetchSchedule = async () => {
    try {
      setLoading(true);

      const { start, end, days } = computeDateRange;

      // Convert filter selections (names) to IDs for API
      const divisionIds = divisions
        .filter((d) => isDivisionSelected(selectedDivisions, d))
        .map((d) => getDivisionId(d))
        .filter((id) => id !== null && id !== undefined && `${id}`.trim() !== "");

      const agencyIds = agencies
        .filter((a) => selectedAgencies.includes(a.name))
        .map((a) => a.id);

      const employmentOfficeIds = employmentOffices
        .filter((o) => selectedEmploymentOffices.includes(o.name))
        .map((o) => o.id);

      const clientIds = clients
        .filter((c) => selectedClients.includes(c.name))
        .map((c) => c.id);

      const staffIds = (selectedStaffIds || []).filter(
        (id) => id !== null && id !== undefined && `${id}`.trim() !== "",
      );

      const positionIds = positions
        .filter((p) => selectedPositions.includes(p.name))
        .map((p) => p.id);

      const shiftStatusIds = shiftStatuses
        .filter((s) => selectedShiftStatuses.includes(s.name))
        .map((s) => s.id);

      const assignmentTypeIds = assignmentTypes
        .filter((t) => selectedAssignmentType === t.name)
        .map((t) => t.id);

      const params = {
        startDate: start,
        endDate: end,
        dateRangeDays: days,
      };

      if (divisionIds.length) {
        // Send both camelCase and snake_case variants to match backend parsers.
        const csv = divisionIds.join(",");
        params.divisionIds = csv;
        params.division_ids = csv;
        params.divisionId = divisionIds[0];
        params.division_id = divisionIds[0];
      }
      if (agencyIds.length) params.agencyIds = agencyIds;
      if (employmentOfficeIds.length) params.employmentOfficeIds = employmentOfficeIds;
      if (clientIds.length) params.clientIds = clientIds;
      if (staffIds.length) params.staffIds = staffIds;
      if (positionIds.length) params.positionIds = positionIds;
      if (shiftStatusIds.length) params.shiftStatuses = shiftStatusIds;
      if (assignmentTypeIds.length) params.assignmentTypes = assignmentTypeIds;
      if (searchQuery) params.search = searchQuery;

      const res = await axios.get(`${BASE_URL}/api/schedules`, {
        params,
        paramsSerializer: { indexes: null },
      });
      console.log("📊 Schedule data received:", res.data);
      setScheduleData(res.data || []);
    } catch (error) {
      console.error("❌ Error fetching schedule data:", error);
      setScheduleData([]);
    } finally {
      setLoading(false);
    }
  };



  useEffect(() => {
    const onLegacyRepairComplete = () => {
      setRefreshTick((t) => t + 1);
    };
    window.addEventListener(
      "atlas:legacy-shift-repair-complete",
      onLegacyRepairComplete,
    );
    return () => {
      window.removeEventListener(
        "atlas:legacy-shift-repair-complete",
        onLegacyRepairComplete,
      );
    };
  }, []);

  // Re-fetch whenever filters/date/search/refresh change
  useEffect(() => {
    fetchSchedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedDivisions,
    selectedAssignmentType,
    selectedAgencies,
    selectedEmploymentOffices,
    selectedClients,
    selectedStaffIds,
    selectedPositions,
    selectedDate,
    selectedDateRange,
    selectedShiftStatuses,
    searchQuery,
    refreshTick,
    computeDateRange,
    divisions,
    agencies,
    employmentOffices,
    clients,
    staff,
    positions,
    shiftStatuses,
    assignmentTypes,
  ]);

  // Transform backend schedule data into UI-friendly format
  const filteredSchedule = useMemo(() => {
    const { start, days } = computeDateRange;
    const activeStatusFilters =
      selectedShiftStatuses && selectedShiftStatuses.length
        ? new Set(selectedShiftStatuses)
        : null;
    const startObj = parseLocalDateInput(start) || new Date();
    const byDate = {};
    scheduleData.forEach((day) => {
      byDate[day.date] = day;
    });
    const result = [];
    for (let i = 0; i < days; i++) {
      const d = new Date(startObj);
      d.setDate(startObj.getDate() + i);
      const iso = formatDate(d);
      const backendDay = byDate[iso];
      const totalShifts =
        backendDay?.totalSlots ?? backendDay?.totalShifts ?? 0;
      // --- Group entries by client/location ---
      const entriesRaw = (backendDay?.shifts?.map((shift) => {
        const formatTimeFromDate = (dateStr, fallbackTimeStr) => {
          if (!dateStr && !fallbackTimeStr) return null;
          try {
            if (dateStr) {
              const timeObj = new Date(dateStr);
              if (!isNaN(timeObj.getTime())) {
                return timeObj.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: getAppDisplayTimezone() });
              }
            }
            if (fallbackTimeStr) {
              const timeObj = new Date(`2000-01-01T${fallbackTimeStr}Z`);
              if (!isNaN(timeObj.getTime())) {
                return timeObj.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: getAppDisplayTimezone() });
              }
            }
            return dateStr || fallbackTimeStr;
          } catch {
            return dateStr || fallbackTimeStr;
          }
        };
        const startTime = formatTimeFromDate(shift.start_date, shift.scheduledStart);
        const endTime = formatTimeFromDate(shift.end_date, shift.scheduledEnd);
        const time = startTime && endTime ? `${startTime} - ${endTime}` : null;
        const sourceShiftId = shift.shiftId || shift.shift_id || shift.id || null;
        const unassignedHistoryRaw =
          shift.unassignedHistory || shift.unassigned_history || [];
        const assignedList = shift.assignedAccounts || shift.assigned_accounts || shift.assignees || shift.assigned || [];
        const assignments = (assignedList || [])
          .filter((acct) => isRealScheduleAssignment(acct))
          .map((acct) => {
            const accountId = acct.accountId || acct.account_id || acct.id;
            const name = acct.name || "Unknown";
            const initials =
              acct.avatarText ||
              acct.initials ||
              (name !== "Unknown"
                ? name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase()
                  .slice(0, 2)
                : "??");

            // Determine the real assignment state.
            // Backend now sends sa.state directly ("assigned", "confirmed", etc.)
            // Fallback: use the legacy boolean 'confirmed' flag.
            const saState = (acct.state || "").toString().toLowerCase();
            const legacyConfirmed =
              acct.confirmed === true ||
              acct.confirmed === 1 ||
              acct.confirmed === "1" ||
              (typeof acct.confirmed === "string" &&
                acct.confirmed.toLowerCase() === "true");

            let assignmentStatus;
            if (shiftAccountStateIsScheduleConfirmed(saState) || legacyConfirmed) {
              assignmentStatus = "confirmed";
            } else if (
              saState === "assigned" ||
              saState === "accepted" ||
              saState === "pending" ||
              saState === "filled" ||
              saState === "verified" ||
              saState === "signed_off"
            ) {
              assignmentStatus = "assigned";
            } else {
              assignmentStatus = "pending";
            }

            return {
              role: shift.positionName || "",
              positionParentName:
                shift.positionParentName || shift.parent_position_name || "",
              sourceShiftId,
              employee: {
                account_id: accountId,
                id: accountId,
                name: name,
                initials: initials,
                phone: acct.phone_mobile || acct.phone || acct.phone_home || "",
                email: acct.email || "",
                payer_name: acct.payer_name || (acct.payer && acct.payer.name) || null,
                reliability: acct.reliability || null,
                utilization: acct.utilization || null,
                hours: acct.hours || null,
              },
              status: assignmentStatus,
              hasTempNote: !!shiftLevelTempNoteText(shift),
              hasAdminNote: !!shiftLevelAdminNoteText(shift),
            };
          });
        const requiredStaff = typeof shift.requiredStaff === "number" ? shift.requiredStaff : 0;
        const assignedCount = assignments.length;
        const rawFilled = shift.confirmedStaff;
        const apiFilled =
          rawFilled === null || rawFilled === undefined || rawFilled === ""
            ? NaN
            : Number(rawFilled);
        const confirmedStaffCount = Number.isFinite(apiFilled)
          ? apiFilled
          : assignments.filter(
              (a) => a.employee && assignmentCountsTowardScheduleFilled(a.status),
            ).length;
        const missingCount = Math.max(0, requiredStaff - assignedCount);
        const unassignedHistory = filterUnassignedHistoryExcludingActiveAssignees(
          unassignedHistoryRaw,
          assignedList,
        );
        const unassignedAssignments = [];
        for (let i = 0; i < missingCount; i++) {
          unassignedAssignments.push({
            role: shift.positionName || "",
            employee: null,
            status: "unassigned",
            sourceShiftId,
          });
        }
        const allAssignments = [...assignments, ...unassignedAssignments];
        const shiftsLabel =
          typeof shift.requiredStaff === "number"
            ? `${confirmedStaffCount}/${shift.requiredStaff}`
            : "0/0";
        const rawState = (shift.shiftState || shift.shift_state || shift.status || "pending").toString().toLowerCase();
        let displayStatus;
        if (rawState === "declined") {
          displayStatus = "Unfilled";
        } else if (rawState === "cancelled") {
          displayStatus = "Cancelled";
        } else if (rawState === "approved" || rawState === "confirmed") {
          displayStatus = "Confirmed";
        } else if (rawState === "tentative") {
          const isUnassigned = assignedCount === 0;
          displayStatus = isUnassigned ? "Unassigned" : "Tentative";
        } else if (
          rawState === "approved" ||
          rawState === "confirmed" ||
          rawState === "assigned" ||
          rawState === "accepted" ||
          rawState === "completed" ||
          rawState === "checkin" ||
          rawState === "checkout" ||
          rawState === "checkedin" ||
          rawState === "checkedout"
        ) {
          const isUnassigned = assignedCount === 0;
          displayStatus = isUnassigned ? "Unassigned" : "Confirmed";
        } else {
          const isUnassigned = assignedCount === 0;
          displayStatus = isUnassigned ? "Unassigned" : "Confirmed";
        }
        // If staff filter is on, keep rows where this shift lists the selected account (API already narrowed by staff;
        // this avoids hiding them when shift-status chips don't match e.g. Tentative vs Confirmed).
        const staffFilterOn =
          Array.isArray(selectedStaffIds) && selectedStaffIds.length > 0;
        const matchesSelectedStaff =
          staffFilterOn &&
          assignedList.some((acct) => {
            if (!isRealScheduleAssignment(acct)) return false;
            const aid = acct?.accountId ?? acct?.account_id ?? acct?.id;
            if (aid == null || aid === "") return false;
            return selectedStaffIds.some(
              (sid) => staffIdKey(sid) === staffIdKey(aid),
            );
          });
        // If staff filter is active, exclude shifts where the selected staff is not actively assigned (e.g. unassigned history only)
        if (staffFilterOn && !matchesSelectedStaff) {
          return null;
        }
        // Frontend shift-status filter: if filters are active and this entry does not match, skip it
        if (
          activeStatusFilters &&
          !activeStatusFilters.has(displayStatus) &&
          !(staffFilterOn && matchesSelectedStaff)
        ) {
          return null;
        }
        const shiftStatus = rawState;
        const slotStartMs = shift.start_date
          ? new Date(shift.start_date).getTime()
          : NaN;
        const slotEndMs = shift.end_date
          ? new Date(shift.end_date).getTime()
          : NaN;
        const entryClientId =
          shift.clientId ?? shift.client_id ?? null;
        const entryStaffRequestId =
          shift.staffRequestId ?? shift.staff_request_id ?? null;
        const entryRow = {
          location: shift.clientName || "Unknown Client",
          sublocation: shift.locationName || shift.locationAddress || "Primary Location",
          clientId: entryClientId,
          staffRequestId: entryStaffRequestId,
          shifts: shiftsLabel,
          time,
          position: shift.positionName || "",
          id: shift.shiftId || shift.shift_id || shift.id || null,
          shift_id: shift.shiftId || shift.shift_id || shift.id || null,
          rawShift: {
            ...shift,
            shift_date: shift.shift_date || iso,
            clientId: entryClientId,
            client_id: entryClientId,
            staffRequestId: entryStaffRequestId,
            staff_request_id: entryStaffRequestId,
            location_lat: shift.location_lat || shift.locationLat || null,
            location_lng: shift.location_lng || shift.locationLng || null,
          },
          // Travel time info comes from the shift; used for travel icon + detail panel
          travel_time_amount: shift.travel_time_amount || null,
          travel_time_type: shift.travel_time_type || null,
          assignments: allAssignments,
          unassignedHistory: Array.isArray(unassignedHistory)
            ? unassignedHistory.map((h) => ({
                ...h,
                sourceShiftId: h.sourceShiftId ?? sourceShiftId,
              }))
            : [],
          shiftStatus,
          displayStatus,
          slotStartMs: Number.isFinite(slotStartMs) ? slotStartMs : 0,
          slotEndMs: Number.isFinite(slotEndMs) ? slotEndMs : 0,
          // Shift-level notes (used for unassigned slots)
          shiftHasTempNote: !!shiftLevelTempNoteText(shift),
          shiftHasAdminNote: !!shiftLevelAdminNoteText(shift),
          isClosedWeek: !!shift.isClosedWeek,
          requiredStaff,
          assignedCount,
          confirmedStaff: confirmedStaffCount,
          location_lat: shift.location_lat || shift.locationLat || null,
          location_lng: shift.location_lng || shift.locationLng || null,
        };
        return applyScheduleEntryHours(entryRow);
      }) || []).filter(Boolean);
      // Group by client/location
      const groupKey = (entry) => `${entry.location}|||${entry.sublocation}`;
      const grouped = {};
      entriesRaw.forEach((entry) => {
        const key = groupKey(entry);
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(entry);
      });
      // Build grouped entries for rendering
      const entries = Object.entries(grouped).map(([key, groupEntries]) => {
        // Group by unique time range
        const timeMap = {};
        groupEntries.forEach(e => {
          const t = e.time || '';
          const sid = e.id || e.shift_id;
          const tagged = (e.assignments || []).map((a) => ({
            ...a,
            sourceShiftId: a.sourceShiftId ?? sid,
          }));
          if (!timeMap[t]) {
            timeMap[t] = {
              ...e,
              hoursNum: 0,
              assignments: tagged,
              confirmedStaff: Number(e.confirmedStaff) || 0,
              parallelShifts: [
                {
                  id: sid,
                  requiredStaff: Number(e.requiredStaff) || 0,
                  rawShift: e.rawShift,
                  shiftStatus: e.shiftStatus,
                  displayStatus: e.displayStatus,
                },
              ],
            };
            if (shiftCountsTowardScheduleHours(e.shiftStatus, e.displayStatus)) {
              timeMap[t].hoursNum =
                (timeMap[t].hoursNum || 0) + shiftNetWorkHours(e.rawShift);
              timeMap[t].hours = formatLegacyScheduleHoursLabel(timeMap[t].hoursNum);
            }
          } else {
            if (shiftCountsTowardScheduleHours(e.shiftStatus, e.displayStatus)) {
              timeMap[t].hoursNum =
                (timeMap[t].hoursNum || 0) + shiftNetWorkHours(e.rawShift);
              timeMap[t].hours = formatLegacyScheduleHoursLabel(timeMap[t].hoursNum);
            }
            timeMap[t].assignments = [...timeMap[t].assignments, ...tagged];
            timeMap[t].unassignedHistory = [
              ...(timeMap[t].unassignedHistory || []),
              ...(e.unassignedHistory || []),
            ];
            const prevReq = Number(timeMap[t].requiredStaff) || 0;
            const addReq = Number(e.requiredStaff) || 0;
            timeMap[t].requiredStaff = prevReq + addReq;
            const prevConf = Number(timeMap[t].confirmedStaff) || 0;
            const addConf = Number(e.confirmedStaff) || 0;
            timeMap[t].confirmedStaff = prevConf + addConf;
            timeMap[t].parallelShifts.push({
              id: sid,
              requiredStaff: Number(e.requiredStaff) || 0,
              rawShift: e.rawShift,
              shiftStatus: e.shiftStatus,
              displayStatus: e.displayStatus,
            });
          }
        });
        // Build unique time slot list (legacy: ascending by shift start time)
        const uniqueSlots = Object.values(timeMap)
          .map((slot) => {
          // Dedupe only true duplicates (same underlying shift + same assignee + role).
          // Parallel unassigned slots must stay distinct (same role, different sourceShiftId).
          const seen = new Set();
          slot.assignments = slot.assignments.filter((a) => {
            const acc = a.employee?.account_id || a.employee?.id || "";
            const key = `${a.sourceShiftId ?? ""}|${acc}|${a.role || ""}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          slot.assignments.sort((a, b) =>
            compareScheduleSlotAssignments(slot, a, b),
          );
          // Recompute assignedCount for this slot
          slot.assignedCount = slot.assignments.filter(a => a.employee).length;
          slot.confirmedCount = slot.assignments.filter(
            (a) => {
              if (!a.employee) return false;
              if (!assignmentCountsTowardScheduleFilled(a.status)) return false;
              const sourceShiftId =
                a.sourceShiftId ?? a.raw?.sourceShiftId ?? slot.id ?? slot.shift_id;
              const slotShiftState = getSlotShiftStateRaw(slot, sourceShiftId);
              return shiftStateCountsTowardConfirmedHeader(slotShiftState);
            },
          ).length;
          // Exclude cancelled parallel shifts from slot.requiredStaff. Unfilled (declined)
          // shifts still need staff, so they stay counted toward the total.
          slot.requiredStaff = (slot.parallelShifts || []).reduce((pSum, p) => {
            const st = String(p.shiftStatus || "").toLowerCase();
            const disp = String(p.displayStatus || "");
            const isBad = st === "cancelled" || disp === "Cancelled";
            if (isBad ) {
              return pSum;
            }
            return pSum + (Number(p.requiredStaff) || 0);
          }, 0);

          slot.shifts = `${slot.confirmedCount}/${slot.requiredStaff || 0}`;
          applyMergedParallelShiftState(slot);
          return slot;
        })
          .sort(compareScheduleTimeSlots);
        const assignedCount = uniqueSlots.reduce((sum, e) => sum + (e.assignedCount || 0), 0);
        const confirmedCount = uniqueSlots.reduce(
          (sum, e) => sum + (e.confirmedCount || 0),
          0,
        );
        const requiredStaff = uniqueSlots.reduce((sum, e) => sum + (e.requiredStaff || 0), 0);
        const totalHours = uniqueSlots.reduce(
          (sum, e) => sum + (Number(e.hoursNum) || 0),
          0,
        );
        const firstEntry = groupEntries[0];
        let groupClientId =
          firstEntry?.clientId ??
          firstEntry?.rawShift?.clientId ??
          firstEntry?.rawShift?.client_id ??
          null;
        if (!groupClientId && firstEntry?.location) {
          const match = clients.find(
            (c) =>
              String(c?.name || "").trim() ===
              String(firstEntry.location).trim(),
          );
          if (match) groupClientId = match.id ?? match.client_id ?? null;
        }
        return {
          location: firstEntry.location,
          sublocation: firstEntry.sublocation,
          clientId: groupClientId,
          entries: uniqueSlots, // all unique time slots for this client/location
          assignedCount,
          confirmedCount,
          requiredStaff,
          totalHours,
        };
      }).sort(compareScheduleClientGroups);
      // Day-level rollup (header CONFIRMED/HOURS) includes every division, Office/iTrain
      // included -- that exclusion is a Dashboard-only convention (utils/dashboardDivisionExclusions.js)
      // for its KPI charts; Schedule is the operational board and Office/iTrain shifts are real
      // work here, so hiding them from the day total produced a wrong, confusing number (e.g.
      // 0/0 when the division filter was set to exactly Office/iTrain).
      const slotsForDayTotals = entries.flatMap((group) => group.entries || []);
      const dayConfirmedCount = slotsForDayTotals.reduce(
        (sum, slot) => sum + (slot.confirmedCount || 0),
        0,
      );
      const dayRequiredStaff = slotsForDayTotals.reduce(
        (sum, slot) => sum + (slot.requiredStaff || 0),
        0,
      );
      const totalHoursNum = slotsForDayTotals.reduce(
        (sum, slot) => sum + (Number(slot.hoursNum) || 0),
        0,
      );
      const dayAnchor = parseDbDateTime(`${iso}T12:00:00Z`);
      result.push({
        day: formatAppIntlLabel(dayAnchor, { weekday: "long" }),
        date: formatAppIntlLabel(dayAnchor, { month: "long", day: "numeric" }),
        isoDate: iso,
        totalHours: formatLegacyScheduleHoursLabel(totalHoursNum),
        confirmedShifts: `${dayConfirmedCount}/${dayRequiredStaff}`,
        entries,
      });
    }
    if (!searchQuery) return result;
    const searchTrimmed = searchQuery.trim();
    return result.map((day) => {
      const filteredEntries = day.entries.filter((entry) =>
        scheduleGroupMatchesSearch(entry, searchTrimmed),
      );
      return {
        ...day,
        entries: filteredEntries,
        totalHours: formatLegacyScheduleHoursLabel(
          sumScheduleGroupHours(filteredEntries),
        ),
      };
    });
  }, [
    scheduleData,
    searchQuery,
    computeDateRange,
    selectedShiftStatuses,
    selectedStaffIds,
    clients,
  ]);

  // After schedule refetch (e.g. Shift Detail modal save: travel time, dates), keep the open right panel in sync.
  useEffect(() => {
    if (!showShiftPanel || !selectedEntry) return;
    const rawSid =
      selectedAssignment?.sourceShiftId ??
      selectedAssignment?.raw?.sourceShiftId ??
      selectedEntry.id ??
      selectedEntry.shift_id ??
      selectedShift?.shift_id ??
      selectedShift?.shiftId ??
      selectedShift?.id;
    const sidNum = Number(rawSid);
    if (!Number.isFinite(sidNum) || sidNum <= 0) return;

    const slot = findMergedSlotByShiftId(filteredSchedule, sidNum);
    if (!slot) return;

    const nextAssignment = pickAssignmentFromSlot(
      slot,
      selectedAssignment,
      sidNum,
    );

    const shiftForPanel =
      resolveSlotRawShift(slot, nextAssignment) || slot.rawShift;
    if (!shiftForPanel) return;

    const nextAssigneeId = Number(
      nextAssignment?.employee?.account_id ||
        nextAssignment?.employee?.id ||
        nextAssignment?.emp?.account_id ||
        nextAssignment?.emp?.id ||
        nextAssignment?.account_id ||
        nextAssignment?.accountId ||
        NaN,
    );
    const nextHasRealAssignee =
      !!nextAssignment &&
      !!(nextAssignment.employee || nextAssignment.emp) &&
      Number.isFinite(nextAssigneeId) &&
      nextAssigneeId > 0;

    const entryCopy = {
      ...slot,
      rawShift: shiftForPanel,
      // After assigning from an open slot, clear unassigned-slot mode so the panel shows assignee + status.
      _isUnassignedSlotView:
        !!selectedEntry._isUnassignedSlotView && !nextHasRealAssignee,
    };

    setSelectedShift(shiftForPanel);
    setSelectedEntry(entryCopy);
    setSelectedAssignment(nextAssignment || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally when filtered grid rebuilds from API only
  }, [filteredSchedule, showShiftPanel]);

  const hasApiShifts = useMemo(() => {
    if (!Array.isArray(scheduleData)) return false;
    return scheduleData.some(
      (day) => Array.isArray(day.shifts) && day.shifts.length > 0
    );
  }, [scheduleData]);

  const hasNarrowingScheduleFilters = useMemo(() => {
    if (searchQuery.trim()) return true;
    if (selectedDivisions.length > 0) return true;
    if (selectedAssignmentType) return true;
    if (selectedAgencies.length > 0) return true;
    if (selectedEmploymentOffices.length > 0) return true;
    if (selectedClients.length > 0) return true;
    if (selectedStaffIds.length > 0) return true;
    if (selectedPositions.length > 0) return true;
    if (selectedShiftStatuses.length > 0) return true;
    return false;
  }, [
    searchQuery,
    selectedDivisions,
    selectedAssignmentType,
    selectedAgencies,
    selectedEmploymentOffices,
    selectedClients,
    selectedStaffIds,
    selectedPositions,
    selectedShiftStatuses,
  ]);

  const hideEmptyScheduleDays = hasNarrowingScheduleFilters;

  const hasVisibleScheduleColumns = useMemo(() => {
    if (!hideEmptyScheduleDays) return true;
    return filteredSchedule.some(
      (d) => Array.isArray(d.entries) && d.entries.length > 0,
    );
  }, [filteredSchedule, hideEmptyScheduleDays]);

  const processedDeepLinkRef = React.useRef(null);

  // Handle URL parameters for shift highlighting (consumes searchParams once to prevent re-opening on close)
  useEffect(() => {
    const shiftId = searchParams.get('shiftId');
    const date = searchParams.get('date');

    if (!shiftId) {
      processedDeepLinkRef.current = null;
      return;
    }

    const shiftIdNum = Number(shiftId);
    if (!Number.isFinite(shiftIdNum) || shiftIdNum <= 0) return;

    if (processedDeepLinkRef.current === shiftId) return;

    let attempts = 0;
    const highlightShift = () => {
      let located = findScheduleSlotInGrid(filteredSchedule, shiftIdNum);

      if (!located) {
        if (date && selectedDate !== String(date).slice(0, 10)) {
          setSelectedDate(String(date).slice(0, 10));
        }
        attempts++;
        if (attempts < 6) {
          setTimeout(highlightShift, 400);
        } else {
          processedDeepLinkRef.current = shiftId;
          setSearchParams({}, { replace: true });
        }
        return;
      }

      processedDeepLinkRef.current = shiftId;
      const { slot, dayIdx, groupIdx } = located;
      const { entryCopy, assignment, shiftForPanel } = buildDeepLinkPanelState(
        slot,
        shiftIdNum,
      );

      setSelectedShift(shiftForPanel);
      setSelectedEntry(entryCopy);
      setSelectedAssignment(assignment);
      setShowShiftPanel(true);

      if (dayIdx >= 0 && groupIdx >= 0) {
        const cardKey = `${dayIdx}-${groupIdx}`;
        setExpandedCards((prev) => ({ ...prev, [cardKey]: true }));
      }

      // Clear searchParams from URL so closing/returning doesn't re-trigger panel or overwrite filters
      setSearchParams({}, { replace: true });
    };

    highlightShift();
  }, [searchParams, filteredSchedule, setSearchParams]);

  // Map of entry id -> entry for quick lookup when opening bulk modal
  const entryMap = useMemo(() => {
    const m = new Map();
    filteredSchedule.forEach(day => {
      (day.entries || []).forEach(entry => {
        if (entry && entry.id) m.set(entry.id, entry);
      });
    });
    return m;
  }, [filteredSchedule]);

  // Bulk update modal state
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [selectedBulkEntries, setSelectedBulkEntries] = useState([]);
  const [closedWeekBulkWarning, setClosedWeekBulkWarning] = useState({ show: false, closedCount: 0, totalCount: 0 });
  const [availabilityBulkWarning, setAvailabilityBulkWarning] = useState({ show: false, conflicts: [] });

  const toggleCardExpansion = (dayIdx, entryIdx) => {
    const key = `${dayIdx}-${entryIdx}`;
    setExpandedCards(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Accepts optional assignment for subcard
  const openShiftDetail = (daySchedule, entry, assignment = null, isUnassignedSlot = false) => {
    const slotRaw = resolveSlotRawShift(entry, assignment);
    const shiftForPanel = slotRaw || entry?.rawShift || daySchedule;
    const slotId =
      assignment?.sourceShiftId ?? assignment?.raw?.sourceShiftId ?? null;
    const slotPosition = resolveScheduleSlotPosition(entry, slotId);
    // Create a copy of entry to avoid mutating the original
    const entryCopy = {
      ...entry,
      rawShift: shiftForPanel,
      position: slotPosition || entry.position,
    };
    // Set flag indicating we're viewing an unassigned slot
    if (isUnassignedSlot) {
      entryCopy._isUnassignedSlotView = true;
    } else {
      entryCopy._isUnassignedSlotView = false;
    }
    setSelectedShift(shiftForPanel);
    setSelectedEntry(entryCopy);
    setSelectedAssignment(assignment); // NEW
    setShowShiftPanel(true);
  };

  const areAllCardsExpanded = () => {
    let totalCards = 0;
    filteredSchedule.forEach((day) => {
      totalCards += day.entries.length;
    });
    return totalCards > 0 && Object.keys(expandedCards).length === totalCards;
  };

  const handleToggleExpandAll = () => {
    if (areAllCardsExpanded()) {
      // Collapse all
      setExpandedCards({});
    } else {
      // Expand all
      const allKeys = {};
      filteredSchedule.forEach((day, dayIdx) => {
        day.entries.forEach((entry, entryIdx) => {
          allKeys[`${dayIdx}-${entryIdx}`] = true;
        });
      });
      setExpandedCards(allKeys);
    }
  };

  const handleRefresh = () => {
    setRefreshTick((t) => t + 1);
  };

  // Unique per underlying shift + assignee (merged rows share entry.id but slots have sourceShiftId).
  const getAssignmentId = (entry, assignment, index) => {
    const shiftId =
      assignment?.sourceShiftId ??
      assignment?.raw?.sourceShiftId ??
      entry?.id ??
      entry?.shift_id;
    if (!shiftId) return null;
    const accountId =
      assignment?.emp?.account_id ||
      assignment?.emp?.id ||
      assignment?.employee?.account_id ||
      assignment?.employee?.id ||
      assignment?.account_id ||
      (assignment?.raw &&
        (assignment.raw.employee?.account_id ||
          assignment.raw.employee?.id ||
          assignment.raw.account_id));
    if (accountId) {
      return `${shiftId}_${accountId}`;
    }
    return `${shiftId}_unassigned_${index}`;
  };

  // Bulk selection handlers - toggle individual assignment selection
  const toggleAssignmentSelection = (entry, assignment, index) => {
    const assignmentId = getAssignmentId(entry, assignment, index);
    if (!assignmentId) return;

    setSelectedAssignmentIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(assignmentId)) {
        newSet.delete(assignmentId);
      } else {
        newSet.add(assignmentId);
      }
      return newSet;
    });
  };

  const toggleDaySelectAll = (dayIdx) => {
    // Get all assignments from all groups in this day
    const daySchedule = filteredSchedule[dayIdx];
    if (!daySchedule) return;

    // Collect all assignment IDs from all entries in all groups
    const allAssignmentIds = [];
    daySchedule.entries.forEach((group) => {
      if (group.entries && Array.isArray(group.entries)) {
        group.entries.forEach((entry) => {
          if (!entry.assignments || !Array.isArray(entry.assignments)) return;
          const displayRows = getScheduleEntryDisplayRows(
            entry,
            resolveScheduleEmployee,
          );
          displayRows.forEach((na, idx) => {
            const assignmentId = getAssignmentId(entry, na, idx);
            if (assignmentId) allAssignmentIds.push(assignmentId);
          });
        });
      }
    });

    if (allAssignmentIds.length === 0) return;

    const allSelected = allAssignmentIds.every(id => selectedAssignmentIds.has(id));

    setSelectedAssignmentIds(prev => {
      const newSet = new Set(prev);
      if (allSelected) {
        // Deselect all assignments in this day
        allAssignmentIds.forEach(id => newSet.delete(id));
      } else {
        // Select all assignments in this day
        allAssignmentIds.forEach(id => newSet.add(id));
      }
      return newSet;
    });

    setDaySelectAll(prev => ({ ...prev, [dayIdx]: !allSelected }));
  };

  const clearAllSelections = () => {
    setSelectedAssignmentIds(new Set());
    setDaySelectAll({});
  };

  const handleBulkUpdate = () => {
    if (bulkModeActive) {
      if (selectedAssignmentIds.size === 0) {
        showError("Please select at least one assignment to update.");
        return;
      }
      // Build selected assignments for the modal from the current schedule
      // Need to collect all assignments that are selected
      // IMPORTANT: Use the same normalization logic as rendering to match assignment IDs
      const selectedAssignments = [];

      filteredSchedule.forEach(day => {
        day.entries.forEach(group => {
          if (group.entries && Array.isArray(group.entries)) {
            group.entries.forEach(entry => {
              if (entry.assignments && Array.isArray(entry.assignments)) {
                const displayRows = getScheduleEntryDisplayRows(
                  entry,
                  resolveScheduleEmployee,
                );

                displayRows.forEach((na, aIdx) => {
                  const assignmentId = getAssignmentId(entry, na, aIdx);
                  if (assignmentId && selectedAssignmentIds.has(assignmentId)) {
                    const emp = na.emp;
                    const isUnassigned = !emp || na.status === 'unassigned';
                    const slotShiftId =
                      na.sourceShiftId ??
                      na.raw?.sourceShiftId ??
                      entry.id ??
                      entry.shift_id;

                    const slotPosition = resolveScheduleSlotPosition(
                      entry,
                      slotShiftId,
                    );
                    const positionLabel =
                      slotPosition ||
                      na._schedulePositionLabel ||
                      (na.raw && na.raw.role) ||
                      '';
                    selectedAssignments.push({
                      shiftId: slotShiftId,
                      entry: {
                        ...entry,
                        location: entry.location || group.location,
                        sublocation: entry.sublocation || group.sublocation,
                        time: entry.time,
                        position: positionLabel,
                        hours: entry.hours,
                        clientName: entry.location || group.location,
                        positionName: positionLabel,
                        rawShift:
                          resolveSlotRawShift(entry, na) ||
                          entry.rawShift ||
                          entry.raw ||
                          null,
                      },
                      assignment: {
                        ...na.raw,
                        employee: emp,
                        emp: emp,
                        raw: na.raw,
                        status: na.status || (isUnassigned ? 'unassigned' : 'pending'),
                        role: (na.raw && na.raw.role) || positionLabel,
                      },
                      assignmentId: assignmentId,
                      rawShift: entry.rawShift || entry.raw || null, // Also include at top level for easy access
                    });
                  }
                });
              }
            });
          }
        });
      });
      console.log('📋 Collected assignments for bulk update:', {
        count: selectedAssignments.length,
        selectedAssignmentIds: Array.from(selectedAssignmentIds),
        assignments: selectedAssignments
      });

      if (selectedAssignments.length === 0) {
        console.error('❌ No assignments collected! Check assignment ID matching.');
        showError('No assignments were found. Please try selecting again.');
        return;
      }

      // Block if any selected shifts belong to a closed week
      const closedCount = selectedAssignments.filter(sa => {
        const entry = sa.entry || {};
        return entry.isClosedWeek === true || entry.isClosedWeek === 1 || entry.isClosedWeek === '1';
      }).length;

      if (closedCount > 0) {
        setClosedWeekBulkWarning({ show: true, closedCount, totalCount: selectedAssignments.length });
        return;
      }

      setSelectedBulkEntries(selectedAssignments);
      setShowBulkModal(true);
    } else {
      // Activate bulk mode
      setBulkModeActive(true);
      setSelectedAssignmentIds(new Set()); // Clear any previous selections
    }
  };

  const handleCancelBulkMode = () => {
    setBulkModeActive(false);
    setSelectedAssignmentIds(new Set());
    setDaySelectAll({});
  };

  const handleCardClickForSelection = (entry, dayIdx, idx, e) => {
    if (bulkModeActive) {
      // In bulk mode, clicking the main card still toggles expansion
      // Selection happens on subcards only
      toggleCardExpansion(dayIdx, idx);
    } else {
      // Normal behavior - toggle expansion
      toggleCardExpansion(dayIdx, idx);
    }
  };


  const clearAllFilters = () => {
    setSelectedDivisions([]);
    setSelectedAssignmentType("");
    setSelectedAgencies([]);
    setSelectedEmploymentOffices([]);
    setSelectedClients([]);
    setSelectedStaffIds([]);
    setSelectedPositions([]);
    setSelectedDate("");
    setSelectedDateRange("7 Days");
    setSelectedShiftStatuses([]);
  };

  const removeFilter = (type, value) => {
    switch (type) {
      case 'division':
        if (typeof value === "undefined") {
          setSelectedDivisions([]);
          break;
        }
        setSelectedDivisions(prev => prev.filter(item => item !== value));
        break;
      case 'assignmentType':
        setSelectedAssignmentType("");
        break;
      case 'agency':
        if (typeof value === "undefined") {
          setSelectedAgencies([]);
          break;
        }
        setSelectedAgencies(prev => prev.filter(item => item !== value));
        break;
      case 'employmentOffice':
        if (typeof value === "undefined") {
          setSelectedEmploymentOffices([]);
          break;
        }
        setSelectedEmploymentOffices(prev => prev.filter(item => item !== value));
        break;
      case 'client':
        if (typeof value === "undefined") {
          setSelectedClients([]);
          break;
        }
        setSelectedClients(prev => prev.filter(item => item !== value));
        break;
      case 'staff':
        if (typeof value === "undefined") {
          setSelectedStaffIds([]);
          break;
        }
        setSelectedStaffIds((prev) =>
          prev.filter((id) => staffIdKey(id) !== staffIdKey(value)),
        );
        break;
      case 'position':
        if (typeof value === "undefined") {
          setSelectedPositions([]);
          break;
        }
        setSelectedPositions(prev => prev.filter(item => item !== value));
        break;
      case 'date':
        setSelectedDate("");
        break;
      case 'dateRange':
        setSelectedDateRange("7 Days");
        break;
      case 'shiftStatus':
        if (typeof value === "undefined") {
          setSelectedShiftStatuses([]);
          break;
        }
        setSelectedShiftStatuses(prev => prev.filter(item => item !== value));
        break;
    }
  };

  const getAllActiveFilters = () => {
    const filters = [];
    selectedDivisions.forEach(div => filters.push({ type: 'division', value: div, label: div }));
    if (selectedAssignmentType) filters.push({ type: 'assignmentType', value: selectedAssignmentType, label: selectedAssignmentType });
    selectedAgencies.forEach(agency => filters.push({ type: 'agency', value: agency, label: agency }));
    selectedEmploymentOffices.forEach(office => filters.push({ type: 'employmentOffice', value: office, label: office }));
    selectedClients.forEach(client => filters.push({ type: 'client', value: client, label: client }));
    selectedStaffIds.forEach((id) => {
      const m = staff.find((s) => staffIdKey(s.id) === staffIdKey(id));
      filters.push({
        type: 'staff',
        value: id,
        label: (m && m.name && String(m.name).trim()) || `#${id}`,
      });
    });
    selectedPositions.forEach(position => filters.push({ type: 'position', value: position, label: position }));
    if (selectedDate) filters.push({ type: 'date', value: selectedDate, label: `Date: ${selectedDate}` });
    if (selectedDateRange) filters.push({ type: 'dateRange', value: selectedDateRange, label: `Range: ${selectedDateRange}` });
    selectedShiftStatuses.forEach(status => filters.push({ type: 'shiftStatus', value: status, label: status }));
    return filters;
  };

  const scheduleChips = useMemo(() => {
    return buildScheduleFilterChips({
      selectedDivisions,
      selectedAssignmentType,
      selectedAgencies,
      selectedEmploymentOffices,
      selectedClients,
      selectedStaffIds,
      selectedPositions,
      selectedDate,
      selectedDateRange,
      selectedShiftStatuses,
      staff,
    });
  }, [
    selectedDivisions,
    selectedAssignmentType,
    selectedAgencies,
    selectedEmploymentOffices,
    selectedClients,
    selectedStaffIds,
    selectedPositions,
    selectedDate,
    selectedDateRange,
    selectedShiftStatuses,
    staff,
  ]);

  return (
    <Layout active="Schedule">
      <fieldset
        disabled={shiftsReadOnly}
        className="border-0 m-0 p-0 min-w-0 block"
      >
      <div className="py-3 px-2 flex flex-col min-h-screen">
        <AtlasPageHeader title="Schedule" />
        <AtlasPageDivider />

        <div
          className={`mt-6 mx-6 p-4 border-2 border-gray-200 rounded-xl h-full flex flex-col min-w-0 bg-gray-50 ${bulkModeActive && hasApiShifts ? "pb-24" : ""} ${filterPanel.contentOffsetClass}`}
        >
          <AtlasListToolbar
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Search clients, locations, staff, or shift ID"
            onFilterClick={filterPanel.toggle}
            filterOpen={filterPanel.isOpen}
            onRefresh={handleRefresh}
            refreshSpinning={loading}
            actions={
              <>
                <button
                  type="button"
                  onClick={handleToggleExpandAll}
                  className={ATLAS_BTN_SECONDARY}
                >
                  {areAllCardsExpanded() ? "COLLAPSE ALL" : "EXPAND ALL"}
                </button>
                <button
                  type="button"
                  onClick={bulkModeActive ? handleCancelBulkMode : handleBulkUpdate}
                  className={
                    bulkModeActive
                      ? "rounded-lg px-4 py-2 text-sm font-medium uppercase text-white transition-colors whitespace-nowrap bg-gray-500 hover:bg-gray-600"
                      : ATLAS_BTN_PRIMARY
                  }
                >
                  {bulkModeActive ? "CANCEL" : "BULK UPDATE"}
                </button>
              </>
            }
            chipBar={
              <AtlasFilterChipBar
                chips={scheduleChips}
                onChipClick={(chip) => {
                  if (chip.fieldId) filterPanel.focusField(chip.fieldId);
                  else filterPanel.open();
                }}
                onRemove={(chip) => removeFilter(chip.key, chip.value)}
                onClearAll={clearAllFilters}
              />
            }
          />

        {hasApiShifts ? (
          hasVisibleScheduleColumns ? (
          <>
            {/* Thin Horizontal Scrollbar */}
            <div
              ref={scrollbarRef}
              className="w-full overflow-x-auto mb-3 h-3"
              onScroll={handleScrollbarScroll}
              style={{ scrollbarWidth: 'thin', scrollbarColor: '#6b7280 #e5e7eb' }}
            >
              <div className="min-w-max h-1">
                {filteredSchedule.map((daySchedule, dayIdx) => {
                  if (hideEmptyScheduleDays && !daySchedule.entries?.length) return null;
                  return (
                    <div key={`scroll-${daySchedule.day}-${dayIdx}`} className="inline-block w-80 mr-4"></div>
                  );
                })}
              </div>
            </div>

            {/* Schedule Container with Fixed Headers and Individual Column Scrollers */}
            <div
              ref={contentRef}
              className="overflow-x-auto"
              onScroll={handleContentScroll}
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
          <div className="flex gap-4 min-w-max">
            {filteredSchedule.map((daySchedule, dayIdx) => {
              if (hideEmptyScheduleDays && !daySchedule.entries?.length) return null;
              return (
              <div key={`${daySchedule.day}-${dayIdx}`} className="flex flex-col w-64 md:w-80 flex-shrink-0">
                {/* Fixed Day Header */}
                <div className="bg-sidebar-active text-white rounded-lg p-1 md:p-2 mb-1 md:mb-2 sticky top-0 z-10">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1 md:gap-2">
                      {bulkModeActive && (() => {
                        // Collect all assignment IDs from all groups in this day
                        const allAssignmentIds = [];
                        daySchedule.entries.forEach((group) => {
                          if (group.entries && Array.isArray(group.entries)) {
                            group.entries.forEach((entry) => {
                              if (!entry.assignments || !Array.isArray(entry.assignments))
                                return;
                              const displayRows = getScheduleEntryDisplayRows(
                                entry,
                                resolveScheduleEmployee,
                              );
                              displayRows.forEach((na, idx) => {
                                const assignmentId = getAssignmentId(entry, na, idx);
                                if (assignmentId) allAssignmentIds.push(assignmentId);
                              });
                            });
                          }
                        });
                        const allSelected = allAssignmentIds.length > 0 && allAssignmentIds.every(id => selectedAssignmentIds.has(id));
                        return (
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={() => toggleDaySelectAll(dayIdx)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-4 h-4 cursor-pointer"
                          />
                        );
                      })()}
                      <div>
                        <div className="font-bold text-xs md:text-sm">{daySchedule.day}</div>
                        <div className="text-[10px] md:text-xs opacity-90">{daySchedule.date}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 md:gap-3 text-[10px] md:text-xs">
                      <div className="text-center">
                        <div className="font-bold text-xs">{daySchedule.totalHours}</div>
                        <div className="text-[10px] md:text-xs opacity-90">HOURS</div>
                      </div>
                      <div className="text-center">
                        <div className="font-bold text-xs">{daySchedule.confirmedShifts}</div>
                        <div className="text-[10px] md:text-xs opacity-90">CONFIRMED</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Scrollable Entry Cards Container */}
                <div className="flex-1 overflow-y-auto max-h-[calc(100vh-300px)] min-h-[400px] space-y-1 md:space-y-2 pr-1 md:pr-2" style={{ scrollbarWidth: 'thin', scrollbarColor: '#6b7280 #e5e7eb' }}>
                  {daySchedule.entries.length === 0 ? (
                    <div className="flex items-center justify-center h-32 text-gray-400 text-sm">
                      <div className="text-center">
                        <div>No shifts scheduled</div>
                        <div className="text-xs mt-1">Create a shift to get started</div>
                      </div>
                    </div>
                  ) : (
                    daySchedule.entries.map((group, idx) => {
                      // group: { location, sublocation, entries: [entry, ...], assignedCount, requiredStaff }
                      const cardKey = `${dayIdx}-${idx}`;
                      const isExpanded = expandedCards[cardKey];
                      return (
                        <div key={idx} className="bg-gray-100 border rounded-lg hover:shadow-md transition-shadow border-gray-200">
                          {/* Main Card - Client/Location */}
                          <div
                            className="p-1 md:p-2 flex items-center justify-between cursor-pointer"
                            onClick={() => handleCardClickForSelection(null, dayIdx, idx)}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1 md:gap-2 mb-0.5 md:mb-1">
                                <span className="font-semibold text-xs md:text-sm text-gray-900 truncate">{group.location}</span>
                              </div>
                              <span className="text-[10px] md:text-xs text-gray-500 truncate block">{group.sublocation}</span>
                            </div>
                            <div className="flex items-center gap-1.5 md:gap-3 text-[10px] md:text-xs flex-shrink-0 ml-1 md:ml-2">
                              <div className="text-center">
                                <div className="font-bold text-xs md:text-sm text-gray-900">
                                  {group.totalHours % 1 === 0 ? group.totalHours : Number(group.totalHours).toFixed(1)}h
                                </div>
                                <div className="text-[10px] md:text-xs text-gray-500">HOURS</div>
                              </div>
                              <div className="text-center">
                                <div className="font-bold text-xs">
                                  <span className="text-green-600">{group.confirmedCount}</span>
                                  <span className="text-black">/{group.requiredStaff}</span>
                                </div>
                                <button
                                  type="button"
                                  title="View client's staff requests for this week"
                                  className="text-[10px] md:text-xs text-sidebar-active underline hover:text-red-700 cursor-pointer focus:outline-none"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const cid = group.clientId;
                                    if (!cid) return;
                                    setStaffRequestsModal({
                                      open: true,
                                      clientId: cid,
                                      clientName: group.location,
                                      weekAnchorDate: daySchedule.isoDate,
                                    });
                                  }}
                                >
                                  SHIFTS
                                </button>
                              </div>
                            </div>
                          </div>
                          {/* Subcards: All time slots for this client/location */}
                          {isExpanded && (
                            <div className="border-t border-gray-200 p-1 md:p-2 bg-gray-200 space-y-1 md:space-y-2">
                              {group.entries.map((entry, slotIdx) => {
                                // ...existing code for rendering a single entry as a subcard...
                                const legacyRows = getScheduleEntryDisplayRows(
                                  entry,
                                  resolveScheduleEmployee,
                                );
                                const timeRange = entry.time;
                                const shiftStRaw = (entry.shiftStatus || "")
                                  .toString()
                                  .toLowerCase();
                                const shiftLevelBad =
                                  shiftStRaw === "cancelled" ||
                                  shiftStRaw === "declined";
                                const shiftLevelTentative =
                                  shiftStRaw === "tentative";
                                return (
                                  <div
                                    key={slotIdx}
                                    data-shift-id={entry.id || entry.shift_id}
                                    className="bg-white rounded border border-gray-200 p-1 md:p-2"
                                  >
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="text-xs text-gray-600 flex items-center gap-1.5">
                                        <span>{timeRange}</span>
                                        {shiftLevelTentative && !shiftLevelBad && (
                                          <span
                                            title="Tentative"
                                            className="inline-flex items-center text-gray-600"
                                            aria-label="Tentative shift"
                                          >
                                            <FaClock className="w-3.5 h-3.5" />
                                          </span>
                                        )}
                                      </div>
                                      <div
                                        className={`text-xs font-semibold px-1.5 py-0.5 rounded flex items-center gap-1 ${
                                          shiftLevelTentative && !shiftLevelBad
                                            ? "bg-gray-200 text-gray-800 border border-gray-300"
                                            : "text-gray-800 bg-gray-100"
                                        }`}
                                      >
                                        {shiftLevelTentative && !shiftLevelBad && (
                                          <FaClock className="w-3 h-3 text-gray-600 shrink-0" aria-hidden />
                                        )}
                                        {entry.hours}
                                      </div>
                                    </div>
                                    {legacyRows.length > 0 ? (
                                      <div className="space-y-1.5">
                                        {(() => {
                                          const renderedHistoryShiftIds = new Set();
                                          return legacyRows.map((na, aIdx) => {
                                          const isUnassigned =
                                            !na.emp || na.status === "unassigned";
                                          const emp = na.emp;
                                          const slotIdForRow =
                                            na.sourceShiftId ??
                                            na.raw?.sourceShiftId ??
                                            null;
                                          const slotStRaw = getSlotShiftStateRaw(
                                            entry,
                                            slotIdForRow,
                                          );
                                          const slotLevelBad =
                                            slotStRaw === "cancelled" ||
                                            slotStRaw === "declined";
                                          const slotLevelTentative =
                                            slotStRaw === "tentative";
                                          const noteShiftId =
                                            slotIdForRow ??
                                            entry.id ??
                                            entry.shift_id;
                                          const unassignedSlotLabel =
                                            na._scheduleDisplaySlotLabel ||
                                            "Open slot";
                                          const emptySlotPrimaryLabel =
                                            slotStRaw === "declined"
                                              ? "Unfilled"
                                              : slotStRaw === "cancelled"
                                                ? "Cancelled"
                                                : "Unassigned";
                                          const slotShiftRawForFlags =
                                            resolveSlotRawShift(entry, na);
                                          const boardNotifyOn =
                                            slotShiftRawForFlags?.notify_staff_change ===
                                              1 ||
                                            slotShiftRawForFlags?.notify_staff_change ===
                                              true ||
                                            slotShiftRawForFlags?.notifyStaffChange ===
                                              true;
                                          const boardIgnoreOn =
                                            slotShiftRawForFlags?.ignore_availability ===
                                              1 ||
                                            slotShiftRawForFlags?.ignore_availability ===
                                              true ||
                                            slotShiftRawForFlags?.ignoreAvailability ===
                                              true;
                                          const boardCarpoolName = String(
                                            slotShiftRawForFlags?.carpool_name ??
                                              slotShiftRawForFlags?.carpoolName ??
                                              "",
                                          ).trim();
                                          const boardCarpoolNote = String(
                                            slotShiftRawForFlags?.carpool_note ??
                                              slotShiftRawForFlags?.carpoolNote ??
                                              "",
                                          ).trim();
                                          const boardCarpoolLine =
                                            boardCarpoolName && boardCarpoolNote
                                              ? `${boardCarpoolName} • ${boardCarpoolNote}`
                                              : boardCarpoolName ||
                                                boardCarpoolNote;
                                          const showBoardCarpool = !!(
                                            boardCarpoolName || boardCarpoolNote
                                          );
                                          const boardRequiredGender = String(
                                            slotShiftRawForFlags?.gender ??
                                              slotShiftRawForFlags?.requiredGender ??
                                              "",
                                          )
                                            .trim()
                                            .toLowerCase();
                                          const shiftHistory = scheduleHistoryForShift(
                                            entry,
                                            noteShiftId,
                                          );
                                          const histKey = String(noteShiftId ?? "");
                                          const showShiftHistory =
                                            shiftHistory.length > 0 &&
                                            histKey &&
                                            !renderedHistoryShiftIds.has(histKey);
                                          if (showShiftHistory) {
                                            renderedHistoryShiftIds.add(histKey);
                                          }
                                          return (
                                            <div key={`slot-row-${aIdx}`} className="space-y-1">
                                              <div
                                                className={`flex items-center justify-between p-2 rounded ${
                                                  bulkModeActive && (() => {
                                                    const assignmentId = getAssignmentId(entry, na, aIdx);
                                                    return assignmentId && selectedAssignmentIds.has(assignmentId);
                                                  })()
                                                    ? 'border-pink-500 bg-pink-50'
                                                    : slotLevelBad
                                                      ? slotStRaw === 'cancelled'
                                                        ? 'bg-orange-50 border border-orange-300'
                                                        : 'bg-red-50 border border-red-300'
                                                      : slotLevelTentative && !isUnassigned
                                                        ? 'bg-gray-100 border border-gray-400'
                                                        : isUnassigned
                                                          ? 'bg-yellow-100 border border-yellow-300'
                                                          : na.status === 'confirmed'
                                                            ? 'bg-green-100 border border-green-500'
                                                            : na.status === 'assigned'
                                                              ? 'bg-green-50 border border-green-300'
                                                              : na.status === 'pending'
                                                                ? 'bg-amber-50/80 border border-amber-200'
                                                                : 'bg-white border border-gray-200'
                                                  } cursor-pointer hover:opacity-90`}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  if (bulkModeActive) {
                                                    toggleAssignmentSelection(entry, na, aIdx);
                                                  } else {
                                                    openShiftDetail(daySchedule, entry, na, isUnassigned);
                                                  }
                                                }}
                                              >
                                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                                {isUnassigned ? (
                                                  <>
                                                    <div
                                                      className={`h-6 w-6 rounded-full bg-gray-100 border flex items-center justify-center flex-shrink-0 ${
                                                        slotLevelBad
                                                          ? slotStRaw === "cancelled"
                                                            ? "border-orange-400"
                                                            : "border-red-400"
                                                          : "border-yellow-300"
                                                      }`}
                                                    >
                                                      <span
                                                        className={`text-xs font-medium ${
                                                          slotLevelBad
                                                            ? slotStRaw === "cancelled"
                                                              ? "text-orange-800"
                                                              : "text-red-800"
                                                            : "text-yellow-700"
                                                        }`}
                                                      >
                                                        ?
                                                      </span>
                                                    </div>
                                                    <div className="flex flex-col min-w-0">
                                                      <span
                                                        className={`text-xs font-medium ${
                                                          slotLevelBad
                                                            ? slotStRaw === "cancelled"
                                                              ? "text-orange-900"
                                                              : "text-red-900"
                                                            : "text-yellow-800"
                                                        }`}
                                                      >
                                                        {emptySlotPrimaryLabel}
                                                      </span>
                                                      <span className="text-xs font-medium text-gray-700 italic inline-flex items-center gap-1">
                                                        {unassignedSlotLabel}
                                                        {boardRequiredGender === "male" ? (
                                                          <FaMale
                                                            className="text-blue-600 not-italic"
                                                            size={14}
                                                            title="Male required"
                                                          />
                                                        ) : boardRequiredGender === "female" ? (
                                                          <FaFemale
                                                            className="text-pink-500 not-italic"
                                                            size={14}
                                                            title="Female required"
                                                          />
                                                        ) : null}
                                                      </span>
                                                    </div>
                                                  </>
                                                ) : (
                                                  <>
                                                    {renderAvatar(emp)}
                                                    <div className="flex flex-col min-w-0">
                                                      <span className="text-xs font-medium text-gray-900 flex items-center gap-1">
                                                        {emp.name || emp.fullName || emp.username || (emp.first_name && `${emp.first_name} ${emp.last_name}`) || 'Unknown'}
                                                        {/* Travel icon: per parallel shift row, not merged entry (first shift only). */}
                                                        {(() => {
                                                          const slotRaw = resolveSlotRawShift(
                                                            entry,
                                                            na,
                                                          );
                                                          const travelAmt =
                                                            slotRaw?.travel_time_amount;
                                                          const travelType =
                                                            slotRaw?.travel_time_type;
                                                          if (
                                                            !shiftRowHasTravel(
                                                              travelAmt,
                                                              travelType,
                                                            )
                                                          ) {
                                                            return null;
                                                          }
                                                          return (
                                                          <span
                                                            className="inline-flex items-center gap-0.5"
                                                            title={travelPayRateSuffix(
                                                              travelType,
                                                              travelAmt,
                                                            )}
                                                          >
                                                            <svg
                                                              xmlns="http://www.w3.org/2000/svg"
                                                              viewBox="0 0 16 16"
                                                              className="w-3 h-3 text-yellow-600"
                                                              aria-hidden="true"
                                                            >
                                                              <g transform="rotate(45 8 8)">
                                                                <rect
                                                                  x="2"
                                                                  y="2"
                                                                  width="12"
                                                                  height="12"
                                                                  rx="1"
                                                                  fill="#b5851d"
                                                                />
                                                                <path
                                                                  d="M6 8h4M8 6l2 2-2 2"
                                                                  fill="none"
                                                                  stroke="#ffffff"
                                                                  strokeWidth="1.4"
                                                                  strokeLinecap="round"
                                                                  strokeLinejoin="round"
                                                                />
                                                              </g>
                                                            </svg>
                                                            <span className="text-[10px] font-semibold text-yellow-700">
                                                              {travelPayRateSuffix(
                                                                travelType,
                                                                travelAmt,
                                                              )}
                                                            </span>
                                                          </span>
                                                          );
                                                        })()}
                                                        {slotLevelTentative && !slotLevelBad && !isUnassigned && (
                                                          <span className="inline-flex items-center px-1 py-0.5 rounded border border-gray-400 bg-gray-200 text-[10px] font-semibold text-gray-700">
                                                            Tentative
                                                          </span>
                                                        )}
                                                        {/* Status badge: per-slot cancelled/declined, else assignment state */}
                                                        {slotLevelBad ? (
                                                          slotStRaw === 'cancelled' ? (
                                                            <span title="Cancelled" className="inline-flex text-orange-600">
                                                              <FaBan style={{ fontSize: '10px' }} />
                                                            </span>
                                                          ) : (
                                                            <span title="Unfilled" className="inline-flex text-red-600">
                                                              <FaTimesCircle style={{ fontSize: '10px' }} />
                                                            </span>
                                                          )
                                                        ) : null}
                                                      </span>
                                                      <span className="text-xs text-gray-500 inline-flex items-center gap-1">
                                                        {na._scheduleDisplaySlotLabel ||
                                                          (na.raw &&
                                                            (na.raw.role ||
                                                              na.raw.position ||
                                                              ""))}
                                                        {boardRequiredGender === "male" ? (
                                                          <FaMale
                                                            className="text-blue-600"
                                                            size={18}
                                                            title="Male required"
                                                          />
                                                        ) : boardRequiredGender === "female" ? (
                                                          <FaFemale
                                                            className="text-pink-500"
                                                            size={18}
                                                            title="Female required"
                                                          />
                                                        ) : null}
                                                      </span>
                                                      {!isUnassigned &&
                                                        (boardIgnoreOn ||
                                                          boardNotifyOn ||
                                                          showBoardCarpool) && (
                                                        <div
                                                          className="text-xs mt-0.5 flex w-full min-w-0 flex-wrap items-start gap-x-1 gap-y-0.5"
                                                          title={
                                                            showBoardCarpool
                                                              ? boardCarpoolLine
                                                              : undefined
                                                          }
                                                        >
                                                          {boardIgnoreOn ? (
                                                            <span
                                                              className="inline-flex shrink-0 pt-0.5"
                                                              title="Availability & requirements being ignored"
                                                              role="img"
                                                              aria-label="Availability and requirements being ignored"
                                                            >
                                                              <MdDoNotDisturbOff
                                                                className="text-gray-500"
                                                                size={10}
                                                              />
                                                            </span>
                                                          ) : null}
                                                          {boardNotifyOn ? (
                                                            <span
                                                              className="inline-flex shrink-0 pt-0.5"
                                                              title="Staff requires notification of change."
                                                              role="img"
                                                              aria-label="Staff requires notification of change"
                                                            >
                                                              <FaFlag
                                                                className="text-red-600"
                                                                size={10}
                                                              />
                                                            </span>
                                                          ) : null}
                                                          {showBoardCarpool ? (
                                                            <button
                                                              type="button"
                                                              className="flex min-w-0 flex-1 items-start gap-1 border-0 bg-transparent p-0 cursor-pointer text-left rounded-sm hover:opacity-90"
                                                              title={boardCarpoolLine}
                                                              onClick={(e) => {
                                                                e.stopPropagation();
                                                                setCarpoolViewer({
                                                                  open: true,
                                                                  name: boardCarpoolName,
                                                                  note: boardCarpoolNote,
                                                                });
                                                              }}
                                                            >
                                                              <FaCar
                                                                className="text-red-600 shrink-0 mt-0.5"
                                                                size={10}
                                                              />
                                                              <span className="min-w-0 flex-1 text-left text-xs text-red-600 [overflow-wrap:anywhere] break-all whitespace-normal">
                                                                {boardCarpoolLine}
                                                              </span>
                                                            </button>
                                                          ) : null}
                                                        </div>
                                                      )}
                                                    </div>
                                                  </>
                                                )}
                                              </div>
                                              <div className="flex items-center gap-1 flex-shrink-0">
                                              {/* Decide which flags to use: per-member when assigned, shift-level when unassigned */}
                                              {(() => {
                                                // Shift-level notes only; hide icons when text is empty (any status).
                                                const slotRaw =
                                                  slotShiftRawForFlags ||
                                                  entry?.rawShift ||
                                                  entry;
                                                const hasTempIcon =
                                                  !!shiftLevelTempNoteText(slotRaw);
                                                const hasAdminIcon =
                                                  !!shiftLevelAdminNoteText(slotRaw);

                                                const accountId =
                                                  (!isUnassigned && (
                                                    (emp && (emp.account_id || emp.id)) ||
                                                    na.account_id ||
                                                    na.accountId ||
                                                    null
                                                  )) ||
                                                  null;

                                                return (
                                                  <>
                                                    {/* Temp staff note icon (red pencil) */}
                                                    {hasTempIcon && (
                                                  <button
                                                    type="button"
                                                    className="p-0.5 rounded-sm border border-red-800 text-red-600 hover:bg-red-50"
                                                    aria-label="Temp staff note"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                    handleShowShiftNote(noteShiftId, accountId, "temp");
                                                    }}
                                                  >
                                                    <svg
                                                      xmlns="http://www.w3.org/2000/svg"
                                                      viewBox="0 0 20 20"
                                                      fill="currentColor"
                                                      className="w-3 h-3"
                                                    >
                                                      <path d="M13.586 3.586a2 2 0 0 1 2.828 2.828l-8.086 8.086A2 2 0 0 1 7.293 15H4a1 1 0 0 1-1-1v-3.293a2 2 0 0 1 .586-1.414l8-8Z" />
                                                      <path d="M5 13h2.586L14 6.586 12.414 5 5 12.414V13Z" />
                                                    </svg>
                                                  </button>
                                                    )}
                                                    {/* Admin note icon (black pencil) */}
                                                    {hasAdminIcon && (
                                                  <button
                                                    type="button"
                                                    className="p-0.5 rounded-sm border border-gray-900 text-gray-900 hover:bg-gray-100"
                                                    aria-label="Admin note"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                    handleShowShiftNote(noteShiftId, accountId, "admin");
                                                    }}
                                                  >
                                                    <svg
                                                      xmlns="http://www.w3.org/2000/svg"
                                                      viewBox="0 0 20 20"
                                                      fill="currentColor"
                                                      className="w-3 h-3"
                                                    >
                                                      <path d="M13.586 3.586a2 2 0 0 1 2.828 2.828l-8.086 8.086A2 2 0 0 1 7.293 15H4a1 1 0 0 1-1-1v-3.293a2 2 0 0 1 .586-1.414l8-8Z" />
                                                      <path d="M5 13h2.586L14 6.586 12.414 5 5 12.414V13Z" />
                                                    </svg>
                                                  </button>
                                                    )}
                                                  </>
                                                );
                                              })()}
                                                {slotLevelBad ? (
                                                  slotStRaw === 'cancelled' ? (
                                                    <FaBan className="h-4 w-4 text-orange-600" title="Cancelled" />
                                                  ) : (
                                                    <FaTimesCircle className="h-4 w-4 text-red-600" title="Unfilled" />
                                                  )
                                                ) : isUnassigned ? (
                                                  <FaTimesCircle className="h-4 w-4 text-yellow-700" title="Open slot" />
                                                ) : slotLevelTentative ? null : na.status === 'confirmed' ? (
                                                  <FaCheck className="h-4 w-4 text-green-600" />
                                                ) : na.status === 'assigned' ? (
                                                  <FaCheck className="h-4 w-4 text-green-400" />
                                                ) : (
                                                  <FaClock
                                                    className="h-4 w-4 text-amber-600"
                                                    title="Tentative"
                                                    aria-label="Tentative"
                                                  />
                                                )}
                                              </div>
                                            </div>
                                              {showShiftHistory
                                                ? shiftHistory.map((h, hi) => {
                                                    const name =
                                                      (h.name || "").trim() || "Unknown";
                                                    const reason = unassignReasonLabel(h.reason);
                                                    const ago =
                                                      (h.modifyAgo || "").trim() ||
                                                      formatUnassignAgo(
                                                        h.modifyDateWall ||
                                                          h.modifyDate ||
                                                          h.modify_date,
                                                      );
                                                    const by = (
                                                      h.unassignedByName ||
                                                      h.unassigned_by_name ||
                                                      ""
                                                    ).trim();
                                                    const affects =
                                                      h.affectsReliability === true ||
                                                      h.affectsReliability === 1 ||
                                                      h.affects_reliability === 1;
                                                    return (
                                                      <div
                                                        key={`unassign-hist-${histKey}-${hi}`}
                                                        className={`pl-8 pr-2 py-1 ${
                                                          hi > 0 ? "border-t border-gray-300 mt-1 pt-2" : ""
                                                        }`}
                                                      >
                                                        <div className="text-xs leading-tight text-gray-700 space-y-0.5">
                                                          <div className="text-xs font-medium text-gray-700 italic">
                                                            {unassignedSlotLabel}
                                                          </div>
                                                          <div className="italic text-gray-900">
                                                            <span>
                                                              {name} <span className="font-semibold">{reason}</span>
                                                              {ago ? `, ${ago}` : ""}
                                                              {by ? ` unassigned by ${by}` : ""}
                                                            </span>
                                                            {affects ? (
                                                              <span className="text-gray-600">
                                                                {" "}
                                                                · Counts towards reliability
                                                              </span>
                                                            ) : null}
                                                            {h.note ? (
                                                              <span className="text-gray-600">
                                                                {" "}
                                                                · NOTE: {h.note}
                                                              </span>
                                                            ) : null}
                                                          </div>
                                                        </div>
                                                      </div>
                                                    );
                                                  })
                                                : null}
                                            </div>
                                          );
                                        });
                                        })()}
                                      </div>
                                    ) : (
                                      <div className="p-2 bg-white rounded border text-center">
                                        <span className="text-xs text-gray-500">No assignments</span>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
            })}
          </div>
        </div>
          </>
          ) : (
            <div className="min-h-[60vh]" aria-hidden="true" />
          )
        ) : (
          <div className="min-h-[60vh]" aria-hidden="true">
            {loading && (
              <div className="flex items-center justify-center pt-24">
                <FaSyncAlt className="h-8 w-8 text-gray-300 animate-spin" aria-label="Loading schedule" />
              </div>
            )}
          </div>
        )}

        </div>

        {/* Simple note viewer modal for schedule view */}
        {noteModal && (() => {
          const modal = (
            <div
              className="fixed inset-0 bg-black bg-opacity-40 z-[50300] flex items-center justify-center p-4"
              onClick={() => setNoteModal(null)}
            >
              <div
                className="bg-white rounded-lg shadow-xl w-full max-w-sm p-4"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="text-base font-semibold mb-3">
                  {noteModal.type === "temp" ? "Note to Temp Staff" : "Admin Note"}
                </h3>
                <div className="mb-4 text-sm text-gray-800 whitespace-pre-line">
                  {noteModal.type === "temp"
                    ? (noteModal.tempText || "No note for temp staff.")
                    : (noteModal.adminText || "No admin note.")}
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    className="px-4 py-1.5 text-sm border rounded hover:bg-gray-100"
                    onClick={() => setNoteModal(null)}
                  >
                    CLOSE
                  </button>
                </div>
              </div>
            </div>
          );
          if (typeof document !== "undefined" && window.ReactDOM && window.ReactDOM.createPortal) {
            return window.ReactDOM.createPortal(modal, document.body);
          }
          return modal;
        })()}

        <RightPanel
          isOpen={showShiftPanel}
          onClose={() => {
            setShowShiftPanel(false);
            setSelectedShift(null);
            setSelectedEntry(null);
            setSelectedAssignment(null);
            if (searchParams.has('shiftId')) {
              setSearchParams({}, { replace: true });
            }
          }}
          title={selectedEntry?.location || "Shift Details"}
          width="620px"
          overlayZIndex={embeddedStaffRequest ? 50500 : 70}
        >
          {selectedShift && selectedEntry && (
            <ShiftDetailPanel
              shift={selectedShift}
              entry={selectedEntry}
              assignment={selectedAssignment}
              isClosedWeek={!!(selectedEntry?.isClosedWeek || selectedShift?.isClosedWeek)}
              readOnly={shiftsReadOnly}
              onOpenPersonnelModal={handleOpenPersonnelModal}
              onOpenShiftModal={handleOpenShiftModal}
              onScheduleRefresh={handleRefresh}
              onScheduleRefreshAwait={fetchSchedule}
              suppressShiftModalAfterAssign
              refreshTick={refreshTick}
            />
          )}
        </RightPanel>

        {/* Personnel Detail Modal */}
        {showPersonnelModal && selectedPersonnel && (
          <PersonnelDetailModal
            person={selectedPersonnel}
            type={mapAccountToModalType(selectedPersonnel)}
            isOpen={showPersonnelModal}
            onClose={() => {
              setShowPersonnelModal(false);
              setSelectedPersonnel(null);
            }}
            overlayZClass={embeddedStaffRequest ? "z-[50510]" : "z-[10100]"}
          />
        )}

        {/* Shift Detail Modal */}
        {showShiftDetailModal && (selectedShiftForModal?.id || selectedShiftForModal?.shift_id) && (
          <ShiftDetailModal
            show={showShiftDetailModal}
            onHide={() => {
              setShowShiftDetailModal(false);
              setSelectedShiftForModal(null);
            }}
            onRefresh={handleRefresh}
            shiftId={selectedShiftForModal?.id || selectedShiftForModal?.shift_id}
            initialIsClosedWeek={selectedShiftForModal?.isClosedWeek}
            overlayZClass={embeddedStaffRequest ? "z-[50510]" : "z-[9999]"}
            onOpenPersonnelModal={handleOpenPersonnelModal}
          />
        )}

        {/* Bulk Update Bottom Bar */}
        {bulkModeActive && hasApiShifts && (
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-300 shadow-lg z-50 p-4">
            <div className="flex items-center justify-between max-w-7xl mx-auto">
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-700">
                  {selectedAssignmentIds.size} of {(() => {
                    let total = 0;
                    filteredSchedule.forEach(day => {
                      day.entries.forEach(group => {
                        if (group.entries && Array.isArray(group.entries)) {
                          group.entries.forEach(entry => {
                            if (entry.assignments && Array.isArray(entry.assignments)) {
                              total += getScheduleEntryDisplayRows(
                                entry,
                                resolveScheduleEmployee,
                              ).length;
                            }
                          });
                        }
                      });
                    });
                    return total;
                  })()} Selected
                </span>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleCancelBulkMode}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 text-sm"
                >
                  CANCEL
                </button>
                <button
                  onClick={handleBulkUpdate}
                  disabled={selectedAssignmentIds.size === 0}
                  className={`px-4 py-2 rounded text-sm ${selectedAssignmentIds.size > 0
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                >
                  BULK UPDATE
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Staff Availability Warning Popup */}
        {availabilityBulkWarning.show && (
          <div className="fixed inset-0 bg-black bg-opacity-40 z-[9999] flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                </div>
                <h3 className="text-base font-semibold text-gray-900">Staff Not Available</h3>
              </div>
              <p className="text-sm text-gray-600 mb-3">
                The following staff member{availabilityBulkWarning.conflicts.length > 1 ? 's are' : ' is'} already assigned to another shift during the new date range and cannot be updated:
              </p>
              <ul className="mb-4 max-h-48 overflow-y-auto space-y-1">
                {availabilityBulkWarning.conflicts.map((c, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm bg-orange-50 border border-orange-200 rounded px-3 py-2">
                    <span className="font-medium text-gray-900">{c.name}</span>
                    <span className="text-xs text-gray-500 ml-auto">
                      {c.conflictStart
                        ? formatAppIntlLabel(c.conflictStart, {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : ""}
                      {c.conflictEnd
                        ? ` – ${formatAppIntlLabel(c.conflictEnd, {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}`
                        : ""}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-gray-500 mb-5">Please deselect unavailable staff or choose a different date range.</p>
              <div className="flex justify-end">
                <button
                  className="px-4 py-2 bg-orange-600 text-white rounded text-sm hover:bg-orange-700"
                  onClick={() => setAvailabilityBulkWarning({ show: false, conflicts: [] })}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Closed Week Bulk Update Warning Popup */}
        {closedWeekBulkWarning.show && (
          <div className="fixed inset-0 bg-black bg-opacity-40 z-[9999] flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                </div>
                <h3 className="text-base font-semibold text-gray-900">Closed Week — Cannot Update</h3>
              </div>
              <p className="text-sm text-gray-600 mb-1">
                {closedWeekBulkWarning.closedCount === closedWeekBulkWarning.totalCount
                  ? 'All selected shifts belong to closed weeks and cannot be updated.'
                  : `${closedWeekBulkWarning.closedCount} of ${closedWeekBulkWarning.totalCount} selected shifts belong to closed weeks.`}
              </p>
              <p className="text-sm text-gray-500 mb-5">
                Please deselect closed-week shifts before running a bulk update.
              </p>
              <div className="flex justify-end">
                <button
                  className="px-4 py-2 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                  onClick={() => setClosedWeekBulkWarning({ show: false, closedCount: 0, totalCount: 0 })}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Update Modal */}
        {showBulkModal && (
          <BulkUpdateModal
            isOpen={showBulkModal}
            entries={selectedBulkEntries}
            onClose={() => setShowBulkModal(false)}
            onApply={async (form) => {
              try {
                // Extract unique shift IDs from selected entries
                const shiftIds = [...new Set(selectedBulkEntries.map(item => {
                  return item.shiftId || item.entry?.id || item.entry?.shift_id || item.id;
                }).filter(Boolean))];

                if (shiftIds.length === 0) {
                  showError('No valid shift IDs found in selected assignments.');
                  return;
                }

                // If a new date range is being set, check assigned staff availability first
                if (form.dateRangeEnabled && (form.startDate || form.endDate)) {
                  const newStart = form.startDate || form.endDate;
                  const newEnd   = form.endDate || form.startDate;

                  // Collect account IDs of assigned staff members in selected entries
                  const accountIds = [...new Set(
                    selectedBulkEntries
                      .map(item => item.assignment?.employee?.account_id || item.assignment?.account_id || item.assignment?.emp?.account_id || null)
                      .filter(Boolean)
                      .map(Number)
                  )];

                  if (accountIds.length > 0 && newStart && newEnd) {
                    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
                    const availResp = await fetch(`${BASE_URL}/api/schedules/check-staff-availability`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                      body: JSON.stringify({ accountIds, startDatetime: newStart, endDatetime: newEnd, excludeShiftIds: shiftIds })
                    });
                    const availData = await availResp.json().catch(() => ({ conflicts: [] }));
                    if (availData.conflicts && availData.conflicts.length > 0) {
                      setAvailabilityBulkWarning({ show: true, conflicts: availData.conflicts });
                      return; // Block the update
                    }
                  }
                }

                // Prepare request body
                const requestBody = {
                  shiftIds: shiftIds,
                  status: form.status || null,
                  startDate: form.dateRangeEnabled && form.startDate ? form.startDate : null,
                  endDate: form.dateRangeEnabled && form.endDate ? form.endDate : null,
                  breakEnabled: form.breakEnabled || false,
                  breakValue: form.breakEnabled ? form.breakValue : null,
                  notifyEnabled: form.notifyEnabled || false,
                  notifyValue: form.notifyEnabled ? form.notifyValue : null,
                };

                // Remove null values
                Object.keys(requestBody).forEach(key => {
                  if (requestBody[key] === null || requestBody[key] === '') {
                    delete requestBody[key];
                  }
                });

                console.log('📤 Sending bulk update request:', requestBody);

                const token = localStorage.getItem('authToken') || localStorage.getItem('token');
                const headers = {
                  'Content-Type': 'application/json',
                  ...(token ? { Authorization: `Bearer ${token}` } : {})
                };

                const response = await fetch(`${BASE_URL}/api/schedules/bulk-update`, {
                  method: 'POST',
                  headers: headers,
                  body: JSON.stringify(requestBody)
                });

                const result = await response.json();

                if (!response.ok) {
                  throw new Error(result.message || 'Failed to update shifts');
                }

                console.log('✅ Bulk update successful:', result);

                // Close modal and reset state
                setShowBulkModal(false);
                setBulkModeActive(false);
                setSelectedAssignmentIds(new Set());
                setDaySelectAll({});

                // Refresh schedule data
                setRefreshTick(prev => prev + 1);

                showSuccess(`Successfully updated ${result.updatedCount || shiftIds.length} shift(s)`);
              } catch (error) {
                console.error('❌ Bulk update error:', error);
                showError(`Failed to update shifts: ${error.message}`);
              }
            }}
          />
        )}
      </div>
      </fieldset>
      {staffRequestsModal?.open && (
        <ScheduleStaffRequestsModal
          open={staffRequestsModal.open}
          onClose={() => setStaffRequestsModal(null)}
          clientId={staffRequestsModal.clientId}
          clientName={staffRequestsModal.clientName}
          weekAnchorDate={staffRequestsModal.weekAnchorDate}
          refreshKey={staffRequestsRefreshKey}
          onView={(row) => {
            const id = row?.id || row?.staff_request_id;
            if (!id) return;
            const { shiftDateFrom, shiftDateTo } =
              getScheduleWeekBounds(staffRequestsModal.weekAnchorDate) || {};
            setStaffRequestsModal(null);
            setEmbeddedStaffRequest({
              id,
              row,
              replicateWeekWindow: { shiftDateFrom, shiftDateTo },
            });
          }}
          onReplicate={(row) => {
            staffRequestsReplicateContextRef.current = {
              clientId: staffRequestsModal.clientId,
              weekAnchorDate: staffRequestsModal.weekAnchorDate,
            };
            // Only list the viewed week's shifts in the replicate popup.
            const { shiftDateFrom, shiftDateTo } =
              getScheduleWeekBounds(staffRequestsModal.weekAnchorDate) || {};
            openReplicateRequestModal(row, { shiftDateFrom, shiftDateTo });
          }}
        />
      )}
      {embeddedStaffRequest && (
        <Jobs
          embedStaffRequestId={embeddedStaffRequest.id}
          embedPreviewRow={embeddedStaffRequest.row}
          embedReplicateWeekWindow={embeddedStaffRequest.replicateWeekWindow}
          onEmbedClose={() => {
            setEmbeddedStaffRequest(null);
            setRefreshTick((t) => t + 1);
            setStaffRequestsRefreshKey((k) => k + 1);
            if (searchParams.has('shiftId')) {
              setSearchParams({}, { replace: true });
            }
          }}
        />
      )}
      {ReplicateModal}
      <CarpoolEditModal
        isOpen={carpoolViewer.open}
        onClose={() => setCarpoolViewer((s) => ({ ...s, open: false }))}
        initialName={carpoolViewer.name}
        initialNote={carpoolViewer.note}
        readOnly
        overlayZClass={embeddedStaffRequest ? "z-[50510]" : "z-[10050]"}
      />
      <AtlasFilterPanel
        isOpen={filterPanel.isOpen}
        onClose={filterPanel.close}
        onClear={clearAllFilters}
        activeCount={scheduleChips.length}
        title="Schedule Filters"
      >
        <ScheduleFilterFields
          focusedFieldId={filterPanel.focusedFieldId}
          onFocusClear={filterPanel.clearFocus}
          
          selectedDivisions={selectedDivisions}
          setSelectedDivisions={setSelectedDivisions}
          selectedAssignmentType={selectedAssignmentType}
          setSelectedAssignmentType={setSelectedAssignmentType}
          selectedAgencies={selectedAgencies}
          setSelectedAgencies={setSelectedAgencies}
          selectedEmploymentOffices={selectedEmploymentOffices}
          setSelectedEmploymentOffices={setSelectedEmploymentOffices}
          selectedClients={selectedClients}
          setSelectedClients={setSelectedClients}
          selectedStaffIds={selectedStaffIds}
          setSelectedStaffIds={setSelectedStaffIds}
          selectedPositions={selectedPositions}
          setSelectedPositions={setSelectedPositions}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          selectedDateRange={selectedDateRange}
          setSelectedDateRange={setSelectedDateRange}
          selectedShiftStatuses={selectedShiftStatuses}
          setSelectedShiftStatuses={setSelectedShiftStatuses}

          divisions={divisions}
          assignmentTypes={assignmentTypes}
          agencies={agencies}
          employmentOffices={employmentOffices}
          clients={clientsForFilterModal}
          staff={staffForFilterModal}
          positions={positions}
          shiftStatuses={shiftStatuses}
          
          staffFilterLoading={staffFilterLoading}
          onStaffQueryChange={setStaffSearchQuery}
        />
      </AtlasFilterPanel>
    </Layout>
  );
}



// Bulk Update Modal Component
function BulkUpdateModal({ isOpen, onClose, entries, onApply }) {
  const [status, setStatus] = useState('');
  const [dateRangeEnabled, setDateRangeEnabled] = useState(false);
  const [startDateObj, setStartDateObj] = useState(null);
  const [endDateObj, setEndDateObj] = useState(null);
  const [datetimePickerTarget, setDatetimePickerTarget] = useState(null);
  const [breakEnabled, setBreakEnabled] = useState(false);
  const [breakValue, setBreakValue] = useState('yes');
  const [notifyEnabled, setNotifyEnabled] = useState(false);
  const [notifyValue, setNotifyValue] = useState('yes');

  const openShiftDatePicker = (target) => {
    setDatetimePickerTarget(target);
  };

  // Pre-fill startDateObj and endDateObj from the first selected entry when modal opens
  React.useEffect(() => {
    if (isOpen && entries && entries.length > 0) {
      const first = entries[0];
      const raw = first.rawShift || first.entry?.rawShift || first.entry?.raw || first.entry || {};
      const s = parseDbDateTime(raw.start_date || raw.startDate || raw.scheduledStart);
      const e = parseDbDateTime(raw.end_date || raw.endDate || raw.scheduledEnd);
      if (s) setStartDateObj(s);
      if (e) setEndDateObj(e);
    }
  }, [isOpen, entries]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 99999 }}>
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} style={{ zIndex: 99990 }}></div>
      <div className="relative bg-white w-full max-w-2xl rounded-lg shadow-xl p-6 max-h-[90vh] overflow-y-auto" style={{ zIndex: 99999 }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Bulk Update Shifts</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)} className="w-full border rounded px-2 py-2 text-sm">
              <option value="">-- No Status Change --</option>
              <option value="Unassigned">Unassigned</option>
              <option value="Tentative">Tentative</option>
              <option value="Confirmed">Confirmed</option>
              <option value="Cancelled">Cancelled</option>
              <option value="Declined">Unfilled</option>
              <option value="Deleted">Deleted</option>
            </select>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input type="checkbox" checked={dateRangeEnabled} onChange={() => setDateRangeEnabled(v => !v)} />
              <span>Start / End</span>
            </label>
            {dateRangeEnabled && (
              <div className="datetime-row flex items-center gap-2 mt-2">
                <div className="flex-1">
                  <label className="text-xs text-gray-500 mb-1 block font-medium">Start</label>
                  <div className="datetime-field flex items-center border border-gray-300 rounded px-2 py-1.5 bg-white relative hover:border-gray-400">
                    <button
                      type="button"
                      className="datetime-calendar-btn text-gray-500 hover:text-gray-700 mr-2 flex items-center shrink-0"
                      aria-label="Open start date and time"
                      onClick={() => openShiftDatePicker('start')}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                        <line x1="16" y1="2" x2="16" y2="6"/>
                        <line x1="8" y1="2" x2="8" y2="6"/>
                        <line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                    </button>
                    <input
                      type="text"
                      className="datetime-input w-full text-sm outline-none bg-transparent cursor-pointer font-medium text-gray-800"
                      readOnly
                      autoComplete="off"
                      placeholder="Select start date & time"
                      value={startDateObj ? formatAppDateTime(startDateObj) : ''}
                      onClick={() => openShiftDatePicker('start')}
                    />
                    {startDateObj && (
                      <button
                        type="button"
                        className="clear-btn text-gray-400 hover:text-gray-600 ml-1 text-base leading-none shrink-0"
                        aria-label="Clear start"
                        onClick={(e) => {
                          e.stopPropagation();
                          setStartDateObj(null);
                        }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
                <span className="datetime-separator text-xs font-medium text-gray-400 mt-5 shrink-0 px-1">to</span>
                <div className="flex-1">
                  <label className="text-xs text-gray-500 mb-1 block font-medium">End</label>
                  <div className="datetime-field flex items-center border border-gray-300 rounded px-2 py-1.5 bg-white relative hover:border-gray-400">
                    <button
                      type="button"
                      className="datetime-calendar-btn text-gray-500 hover:text-gray-700 mr-2 flex items-center shrink-0"
                      aria-label="Open end date and time"
                      onClick={() => openShiftDatePicker('end')}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                        <line x1="16" y1="2" x2="16" y2="6"/>
                        <line x1="8" y1="2" x2="8" y2="6"/>
                        <line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                    </button>
                    <input
                      type="text"
                      className="datetime-input w-full text-sm outline-none bg-transparent cursor-pointer font-medium text-gray-800"
                      readOnly
                      autoComplete="off"
                      placeholder="Select end date & time"
                      value={endDateObj ? formatAppDateTime(endDateObj) : ''}
                      onClick={() => openShiftDatePicker('end')}
                    />
                    {endDateObj && (
                      <button
                        type="button"
                        className="clear-btn text-gray-400 hover:text-gray-600 ml-1 text-base leading-none shrink-0"
                        aria-label="Clear end"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEndDateObj(null);
                        }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={breakEnabled} onChange={() => setBreakEnabled(v => !v)} />
              <span>Break</span>
            </label>
            {breakEnabled && (
              <select value={breakValue} onChange={e => setBreakValue(e.target.value)} className="w-full border rounded px-2 py-2 mt-2 text-sm">
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            )}
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={notifyEnabled} onChange={() => setNotifyEnabled(v => !v)} />
              <span>Staff Requires Notification of Change</span>
            </label>
            {notifyEnabled && (
              <select value={notifyValue} onChange={e => setNotifyValue(e.target.value)} className="w-full border rounded px-2 py-2 mt-2 text-sm">
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold">Selected Shifts</div>
              <div className="text-xs text-gray-500">{(entries || []).length} assignment{(entries || []).length !== 1 ? 's' : ''} selected</div>
            </div>
            <div className="max-h-60 overflow-auto border border-gray-200 rounded">
              {!entries || entries.length === 0 ? (
                <div className="p-4 text-xs text-gray-500 text-center">No shifts selected</div>
              ) : (
                <table className="w-full text-sm table-auto border-collapse">
                  <thead className="bg-gray-50 sticky top-0 border-b border-gray-200">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Shift</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Scheduled</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Client &amp; Position</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Staff</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(entries || []).map((item, idx) => {
                      // Extract data from item structure - handle both formats
                      const entry = item.entry || item;
                      const assignment = item.assignment || null;
                      const rawShift = entry?.rawShift || item.rawShift || null;

                      // Get shift ID - try multiple sources
                      const shiftId = entry?.id || entry?.shift_id || item.shiftId || item.id || entry?.shiftId || `Shift-${idx + 1}`;

                      // Extract staff name from assignment
                      let staffName = '—';
                      if (assignment) {
                        // Try multiple ways to get employee
                        const emp = assignment.employee || assignment.emp || (assignment.raw && assignment.raw.employee) || null;
                        if (emp) {
                          if (emp.name) {
                            staffName = emp.name;
                          } else if (emp.first_name || emp.last_name) {
                            staffName = `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
                          } else if (emp.fullName) {
                            staffName = emp.fullName;
                          } else if (emp.username) {
                            staffName = emp.username;
                          }
                        }
                      }

                      // Format scheduled time - use simple format: "9:00 AM - 4:30 PM"
                      let scheduledTime = '—';
                      // First try entry.time (already formatted)
                      if (entry?.time) {
                        scheduledTime = entry.time;
                      }
                      // Then try rawShift dates
                      else if (rawShift) {
                        const startDate = rawShift.start_date || rawShift.startDate || rawShift.startDateIso || rawShift.scheduledStart || null;
                        const endDate = rawShift.end_date || rawShift.endDate || rawShift.endDateIso || rawShift.scheduledEnd || null;

                        if (startDate && endDate) {
                          try {
                            const start = new Date(startDate);
                            const end = new Date(endDate);

                            if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
                              const formatTime = (date) => {
                                const hours = date.getHours();
                                const minutes = date.getMinutes();
                                const hour12 = hours % 12 || 12;
                                const ampm = hours >= 12 ? 'PM' : 'AM';
                                if (minutes === 0) {
                                  return `${hour12}:00 ${ampm}`;
                                }
                                return `${hour12}:${String(minutes).padStart(2, '0')} ${ampm}`;
                              };
                              scheduledTime = `${formatTime(start)} - ${formatTime(end)}`;
                            }
                          } catch (e) {
                            console.warn('Error formatting time:', e);
                          }
                        }
                      }

                      // Extract client and position - try multiple sources
                      const clientName = entry?.location || entry?.clientName || entry?.client || rawShift?.clientName || entry?.sublocation || 'Client';
                      const positionName = entry?.position || entry?.positionName || assignment?.role || (assignment?.raw && assignment.raw.role) || rawShift?.positionName || '—';

                      return (
                        <tr key={item.assignmentId || `${shiftId}-${idx}`} className="border-t hover:bg-gray-50">
                          <td className="px-3 py-2 align-top">
                            <div className="text-xs font-medium">{String(shiftId)}</div>
                          </td>
                          <td className="px-3 py-2 align-top">
                            <div className="text-xs">{scheduledTime}</div>
                          </td>
                          <td className="px-3 py-2 align-top">
                            <div className="text-xs font-medium">{clientName}</div>
                            <div className="text-xs text-gray-500">{positionName}</div>
                          </td>
                          <td className="px-3 py-2 align-top">
                            <div className="text-xs">{staffName}</div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-gray-200 flex items-center justify-between">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:bg-gray-50">CANCEL</button>
          <div className="flex gap-3">
            <button
              onClick={() => {
                const sDateFormatted = startDateObj ? toMysqlUtcWallString(startDateObj) : '';
                const effectiveEndObj = endDateObj || (startDateObj ? new Date(startDateObj.getTime() + 8 * 60 * 60 * 1000) : null);
                const eDateFormatted = effectiveEndObj ? toMysqlUtcWallString(effectiveEndObj) : '';

                onApply({
                  status,
                  dateRangeEnabled,
                  startDate: sDateFormatted,
                  endDate: eDateFormatted,
                  breakEnabled,
                  breakValue,
                  notifyEnabled,
                  notifyValue
                });
              }}
              className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 font-medium"
            >
              UPDATE
            </button>
          </div>
        </div>

        {datetimePickerTarget && (
          <DateTimePickerPanel
            key={datetimePickerTarget}
            value={
              datetimePickerTarget === 'start'
                ? (startDateObj || new Date())
                : (endDateObj || startDateObj || new Date())
            }
            mode={datetimePickerTarget === 'start' ? 'from' : 'to'}
            onChange={(date) => {
              if (datetimePickerTarget === 'start') {
                setStartDateObj(date);
                if (!endDateObj) {
                  setEndDateObj(new Date(date.getTime() + 8 * 60 * 60 * 1000));
                }
              } else {
                setEndDateObj(date);
              }
            }}
            onDone={() => setDatetimePickerTarget(null)}
            onContinue={() => setDatetimePickerTarget('end')}
            onBack={() => setDatetimePickerTarget('start')}
          />
        )}
      </div>
    </div>
  );
}




